/**
 * Dashboard – /dashboard
 *
 * Haupt-Spielansicht: Jobs, Stats, Economy.
 * Nur für eingeloggte Spieler zugänglich (Auth-Guard in router.ts).
 *
 * Design: Terminal-UI, Share Tech Mono, keine Emojis, keine Gradienten.
 * Layout: Mobile-first Grid (375px Basis).
 */

import '../styles/dashboard.css';
import '../styles/community.css';
import '../styles/org.css';
import { navigate } from '../router.js';
import { gameState, type PlayerSnapshot, type OfflineSummary } from '../state/gameState.js';
import { api, ApiError } from '../services/api.js';
import { logoutUser } from '../services/auth.js';
import { setBottomNavActiveTab } from '../components/BottomNav.js';
import { renderCommunityTab, mountCommunityTab, resetCommunityState } from '../pages/community.js';
import { renderOrgTab, mountOrgTab, cleanupOrgTab } from '../pages/org.js';
import { renderStreamTab, mountStreamTab, cleanupStreamTab } from '../pages/community/stream.js';
import { renderImmobilienTab, mountImmobilienTab, cleanupImmobilien } from '../pages/immobilien/geschaefte.js';
import { renderLagerTab, mountLagerTab, cleanupLager } from '../pages/immobilien/lager.js';
import { renderVersteckeTab, mountVersteckeTab, cleanupVerstecke } from '../pages/immobilien/verstecke.js';
import { renderCrimeOpsTab, mountCrimeOpsTab, cleanupCrimeOps, syncCrimeOpsUrlToSelection } from '../pages/business/crimeOps.js';
import { prepareKladdeTab, renderKladdeTab, mountKladdeTab } from './kladde.js';
import { prepareBrettTab, renderBrettTab, mountBrettTab, resetBrettTab } from './brett.js';
import {
  getCrimeOpsSectionForPath,
  getJobsViewForPath,
  isCrimeOpsPath,
  JOBS_VIEW_PATHS,
  type JobsView,
} from '../utils/dashboard-routes.js';
import { renderMarktTab, mountMarktTab, cleanupMarkt } from '../pages/business/markt.js';
import { renderLagerhausTab, mountLagerhausTab, cleanupLagerhaus } from '../pages/business/lagerhaus.js';
import { renderBoerseTab, mountBoerseTab, cleanupBoerse } from '../pages/business/boerse.js';
import { renderProfileTab, mountProfileTab } from './profile.js';
import { showEventOutcomePanel, type EventOutcome as EventOutcomePanelData } from '../components/event-outcome-panel.js';
import { showPoliceEventModal, type PoliceEventData } from '../components/PoliceEventModal.js';
import { initHamburgerMenu, toggleHamburgerMenu, HAMBURGER_ICON_SVG } from '../components/HamburgerMenu.js';
import { initNotificationBell, toggleNotificationBell, renderNotificationBellButton } from '../components/NotificationBell.js';
import { initScrollToTop, updateScrollToTopContainer } from '../components/ScrollToTop.js';
import { openPfandModal, removePfandModal } from '../components/PfandModal.js';
import { preparePfandBetriebView, renderPfandBetriebViewShell, mountPfandBetriebView, resetPfandBetriebViewState } from '../components/PfandBetriebModal.js';
import { prepareWerkstattView, renderWerkstattViewShell, mountWerkstattView, resetWerkstattViewState } from '../components/WerkstattBetriebView.js';
import { prepareWerkstattCraftView, renderWerkstattCraftViewShell, mountWerkstattCraftView, resetWerkstattCraftViewState } from '../components/WerkstattCraftView.js';
import { renderKladdePanel } from '../components/KladdePanel.js';
import { fetchKladdeState, dismissKladdePanel, dismissKladdeHint, type KladdeState } from '../services/kladde.js';
import { selectVisibleBetriebeShortcuts } from './dashboard.betriebe-shortcuts.js';
import { showRequiredBadge } from '../utils/required-badge.js';
import { formatItemKey } from '../utils/formatters.js';
import { getWantedLevelLabel, getNextWantedThreshold } from '../utils/wanted-labels.js';
import { showToast as showGlobalToast } from '../utils/toast.js';
import { renderPlayerBadges, getPrestigeColorVar } from '../utils/award-badges.js';
import { getSoundVolumeFactor } from '../services/sound.service.js';
import { calcNoticeDisplayMs } from '../utils/notice-duration.js';

// ─── Lokale Typen (API-Response-Strukturen) ───────────────────────────────────

interface JobPlayerState {
  isUnlocked: boolean;
  isInCooldown: boolean;
  cooldownEndsAt: string | null;
  jobLevel: number;
  jobXp: number;
  jobPhase: number;   // Phase 2.7: phasenspezifische XP-Kurve
  // NT-PFAND-IMPERIUM-01: nur bei pfandflaschen_imperium gesetzt — Job-Card wird komplett
  // ausgeblendet statt als "GESPERRT"-Card gezeigt, solange das zusammengesetzte Gate
  // (Char-Level + Sammler-Job-Level, serverseitig via ensurePfandBetrieb geprüft) nicht erfüllt ist.
  isHidden: boolean;
  // Namen noch fehlender Pflicht-Talente (leer, wenn keine Anforderung besteht oder alle
  // erfüllt sind) — Job-Card zeigt dann "TALENT ERFORDERLICH" statt eines klickbaren STARTEN.
  missingTalentNames: string[];
}

interface JobStatusItem {
  id: string;
  name: string;
  description: string;
  phase: number;
  rewardCents: number;
  /** NT-JOB-LAEUFER-01: zusätzlicher, immer BLACK_MONEY Bonus-Betrag pro Schicht (0 = keiner). */
  rewardBlackCents: number;
  rewardXp: number;
  jobXpPerShift: number;
  tickIntervalSeconds: number;
  shiftDurationMinutes: number;
  unlockLevel: number;
  isCrimeJob: boolean;
  rewardCurrency: string;   // 'EUR' oder 'BLACK_MONEY'
  wantedPointsDelta: number; // WantedPoints-Anstieg pro Schicht
  playerState: JobPlayerState;
}

interface ActiveJobTickState {
  id: string;
  jobId: string;
  jobName: string;
  startedAt: string;
  shiftEndsAt: string | null;   // Backend kann null liefern wenn Schicht noch nicht gesetzt
  lastTickAt: string | null;
  nextTickInSeconds: number;
  jobLevel: number;
  rewardCents: number;
  rewardXp: number;
  jobXpPerShift: number;
  tickIntervalSeconds?: number; // Nicht im /status-Endpunkt, nur in /start-Response
}

interface QueueEntry {
  id: string;
  jobId: string;
  jobName: string;
  position: number;
}

interface LevelUpResult {
  oldLevel: number;
  newLevel: number;
  levelsGained: number;
  newlyUnlockedJobIds: string[];
}

interface JobLevelUpResult {
  newJobLevel: number;
  milestone: string | null;
}


interface EventPayload {
  jobEventLogId: string;
  type: 'PASSIVE' | 'DECISION' | 'REACTION';
  name: string;
  description: string;
  bonusCents: string | null;
  reputationDelta: number | null;
  logMessage: string | null;
  options: { key: string; label: string }[] | null;
  expiresAt: string | null;
  itemDropResults?: ItemDropSummary[] | null;
}

interface ItemDropSummary {
  dropped: boolean;
  itemType: string | null;
  itemLabel: string | null;
  quantity: number;
  destinationType: string | null;
  autoSoldCents?: number;
}

interface EventResolutionResult {
  eventId: string;
  choiceKey: string;
  outcome: { logMessage: string; eurDelta?: number; reputationDelta?: number; wantedLevelDelta?: number };
  appliedEffects: { eurDelta?: number; crimeStatDelta?: number; reputationDelta?: number; wantedLevelDelta?: number; shiftTimePenaltySeconds?: number; shiftEarningsModifier?: number; itemDropResults?: ItemDropSummary[] | null };
}

interface TickResult {
  tickProcessed: boolean;
  progressPercent: number;      // 0–95 (akkumulierter Verdienst / Schichtverdienst)
  nextTickInSeconds: number;
  shiftEndsAt: string | null;
  event: EventPayload | null;
  patrolEvent?: PoliceEventData; // FL3+ Midshift-Polizei-Event
}

interface ShiftEventEntry {
  name: string;
  bonusCents: string | null;
  timestamp: string;
}

interface CompleteJobResult {
  summary: {
    jobName: string;
    shiftDuration: number;
    completionPercent: number;
    baseRewardCents: string;
    jobLevelMultiplier: number;
    talentMultiplier: number;
    crimeStatMultiplier: number;
    debtRepaymentCents: string;
    finalRewardCents: string;
    xpEarned: number;
    jobXpEarned: number;
    jobLevelUp: JobLevelUpResult | null;
    playerLevelUp: LevelUpResult | null;
    events: ShiftEventEntry[];
    eventBonusTotal: string;
    newBalance: string;
    /** NT-JOB-LAEUFER-01: zusätzlicher, immer BLACK_MONEY Bonus-Anteil ("0" = keiner). */
    bonusBlackMoneyCents: string;
    newBlackMoneyBalance: string;
    cooldownEndsAt: string;
    queueStarted: string | null;
    queueError: string | null;
    itemDrop: {
      dropped: boolean;
      itemType: string | null;
      itemLabel: string | null;
      quantity: number;
      targetVehicleId: string | null;
      targetStorageId: string | null;
      slotIndex: number | null;
      overflowReason: 'NO_DROP' | 'NO_STORAGE' | 'STORAGE_FULL' | null;
      reason: 'NO_STORAGE' | 'STORAGE_FULL' | null;
      // HAND/LAGER: pfandflasche-Sonderpfad (Chris-Bugreport 2026-08-10, Backend
      // item-drop.service.ts::grantJobCompletionItemDrop()).
      destinationType: 'VEHICLE' | 'WAREHOUSE' | 'AUTO_SOLD' | 'HAND' | 'LAGER' | null;
      autoSoldCents?: number;
    };
    // NT-PFAND-SCHICHT-01: Flaschensammeln mit dem Einkaufswagen — null, wenn nicht zutreffend.
    pfandSchicht?: { collected: number; leftBehind: number } | null;
    newWantedLevel?: number;
    newWantedPoints?: number;
    policeEventTriggered?: boolean;
    policeEventData?: PoliceEventData;
  };
}

interface TalentEffect {
  incomeMultiplier?: number;
  deceptionEventSuccessRate?: number;
  negotiationsUnlocked?: boolean;
  dealBonus?: number;
  passiveReputationMultiplier?: number;
}

interface TalentNode {
  id: string;
  name: string;
  description: string;
  branch: string;
  tier: number;
  pointCost: number;
  maxPoints: number;
  prerequisiteId?: string;
  prerequisiteIds?: string[];
  minLevel?: number;
  cost: { eur: number; blackMoney: number };
  effect: TalentEffect;
  investedPoints: number;
  prerequisiteMet: boolean;
  isMaxed: boolean;
  isActive: boolean;
}

interface DebtStatus {
  hasDebt: boolean;
  debtNox: string;
  debtBlackMoney: string;
  debtSource: string | null;
  incomeDebuffPercent: number;
}

interface RepayDebtResult {
  repaidEur: string;
  repaidBlackMoney: string;
  remainingDebtEur: string;
  remainingDebtBlackMoney: string;
  debtCleared: boolean;
}

interface InvestPointsResult {
  learning: {
    talentId: string;
    talentName: string;
    completesAt: string;
    learnTimeMs: number;
    tier: 'TIER_1' | 'TIER_2' | 'TIER_3';
  };
  costCharged: { eur: string; blackMoney: string };
  debtCreated: { eur: string; blackMoney: string } | null;
}

interface CurrentLearning {
  talentId: string;
  talentName: string;
  startedAt: string;
  completesAt: string;
  progressPercent: number;
  remainingMs: number;
  tier: 'TIER_1' | 'TIER_2' | 'TIER_3';
  skipCostScheine: number;
}

interface LearningStatusResponse {
  currentLearning: CurrentLearning | null;
  completedButUncollected: {
    talentId: string;
    talentName: string;
    completedSinceMs: number;
  }[];
}

interface SkipLearningResult {
  talent: { talentId: string; talentName: string };
  scheineSpent: number;
  scheineRemaining: number;
  completedAt: string;
}

interface TalentTreeData {
  availableTalentPoints: number;
  branches: Record<TalentBranchKey, TalentNode[]>;
}

const TALENT_BRANCH_KEYS = [
  'SILBERZUNGE',
  'KAEPSELE',
  'KOCHKURS',
  'GRUENER_DAUMEN',
  'SCHUPPENMEISTER',
  'NEBENJOBBER',
  'MENSCHENFUEHRER',
  'NACKENMUSKEL',
  'GRAUE_MAUS',
  'PLATZHIRSCH',
  'HINTERZIMMER',
] as const;

type TalentBranchKey = (typeof TALENT_BRANCH_KEYS)[number];

/**
 * Talent-Audit-Bugfix: PLATZHIRSCH/HINTERZIMMER setzen auf das noch nicht gebaute
 * Organisations-System (Phase 5) — Investieren ist serverseitig gesperrt
 * (talent.service.ts::investPoints, PHASE_NOT_AVAILABLE). Duplikat statt Shared-Import
 * (etabliertes Pattern für reine Anzeige-Werte in diesem Repo).
 */
const PHASE5_LOCKED_BRANCHES: readonly TalentBranchKey[] = ['PLATZHIRSCH', 'HINTERZIMMER'];

const TALENT_BRANCH_META: Record<TalentBranchKey, { label: string; icon: string; motto: string; themes: { label: string; color: string }[] }> = {
  SILBERZUNGE: {
    label: 'SILBERZUNGE', icon: '&#9830;',
    motto: 'Die Wahrheit ist ein Werkzeug. Du benutzt lieber das andere.',
    themes: [
      { label: 'Täuschung', color: 'gold' },
      { label: 'Deals', color: 'gold' },
      { label: 'Ruf', color: 'gold' },
    ],
  },
  KAEPSELE: {
    label: 'KÄPSELE', icon: '&#9670;',
    motto: 'Google ist dein bester Freund. Und der einzige der nicht redet.',
    themes: [
      { label: 'IT', color: 'blue' },
      { label: 'Hacking', color: 'blue' },
      { label: 'Geldwäsche', color: 'purple' },
      { label: 'Taktik', color: 'blue' },
    ],
  },
  KOCHKURS: {
    label: 'KOCHKURS', icon: '&#9651;',
    motto: 'Rezept nicht gefunden. Improvisation läuft.',
    themes: [
      { label: 'Chemie', color: 'purple' },
      { label: 'Labor-Rezepte', color: 'purple' },
      { label: 'Produktion', color: 'purple' },
    ],
  },
  GRUENER_DAUMEN: {
    label: 'GRÜNER DAUMEN', icon: '&#9752;',
    motto: 'Pflanzen brauchen Liebe. Und gute Belüftung.',
    themes: [
      { label: 'Anbau', color: 'green' },
      { label: 'Sorten', color: 'green' },
      { label: 'Ernte', color: 'green' },
    ],
  },
  SCHUPPENMEISTER: {
    label: 'SCHUPPENMEISTER', icon: '&#9638;',
    motto: 'Ordnung ist das halbe Leben. Die andere Hälfte ist Platz für mehr Zeug.',
    themes: [
      { label: 'Lager', color: 'orange' },
      { label: 'Lieferungen', color: 'orange' },
      { label: 'Schmuggel', color: 'orange' },
    ],
  },
  NEBENJOBBER: {
    label: 'NEBENJOBBER', icon: '&#9654;',
    motto: 'Zeit ist Geld. Und beides kann man gleichzeitig verschwenden.',
    themes: [
      { label: 'Job-Queue', color: 'orange' },
      { label: 'Cooldowns', color: 'muted' },
    ],
  },
  MENSCHENFUEHRER: {
    label: 'MENSCHENFÜHRER', icon: '&#9873;',
    motto: 'Alleine dealst du. Mit Leuten dealst du groß.',
    themes: [
      { label: 'Dealer-NPCs', color: 'purple' },
      { label: 'Mitarbeiter', color: 'gold' },
      { label: 'Kontakte', color: 'gold' },
    ],
  },
  NACKENMUSKEL: {
    label: 'NACKENMUSKEL', icon: '&#9650;',
    motto: 'Reden ist Silber. Schweigen und groß sein ist Gold.',
    themes: [
      { label: 'Physische Jobs', color: 'red' },
      { label: 'Einschüchterung', color: 'red' },
      { label: 'Org-Kampf', color: 'red' },
    ],
  },
  GRAUE_MAUS: {
    label: 'GRAUE MAUS', icon: '&#9679;',
    motto: 'Die gefährlichste Person im Raum ist die die niemand bemerkt.',
    themes: [
      { label: 'Polizeirisiko', color: 'muted' },
      { label: 'Fahndung', color: 'red' },
      { label: 'Undercover', color: 'muted' },
    ],
  },
  PLATZHIRSCH: {
    label: 'PLATZHIRSCH', icon: '&#9733;',
    motto: 'Dein Name reicht. Meistens.',
    themes: [
      { label: 'Bezirke', color: 'gold' },
      { label: 'Org', color: 'gold' },
      { label: 'Territorium', color: 'gold' },
    ],
  },
  HINTERZIMMER: {
    label: 'HINTERZIMMER', icon: '&#9632;',
    motto: 'Macht ist nicht was du hast. Macht ist was andere denken dass du hast.',
    themes: [
      { label: 'PI-Ressource', color: 'purple' },
      { label: 'Richter', color: 'purple' },
      { label: 'Medien', color: 'blue' },
      { label: 'Gesetze', color: 'purple' },
    ],
  },
};

interface JobStatusResponse {
  allJobs: JobStatusItem[];
  activeJobs: ActiveJobTickState[]; // Backend gibt Array zurück, nicht Singular
  cooldowns: { jobId: string; cooldownEndsAt: string | null }[];
  queue: QueueEntry[];
  queueMaxSlots: number;
}

type Tab = 'jobs' | 'talents' | 'items' | 'warehouse' | 'crime' | 'immobilien' | 'stream' | 'market' | 'community' | 'profile'
         | 'immobilien_lager' | 'immobilien_verstecke' | 'business_markt' | 'business_boerse' | 'org'
         | 'kladde' | 'brett';

// ─── Modul-Level-State ────────────────────────────────────────────────────────
// Lebt so lange das Dashboard gemountet ist; wird in cleanup() zurückgesetzt.

let pollingTimer: ReturnType<typeof setInterval> | null = null;   // 60s Status-Re-Sync
let progressTimer: ReturnType<typeof setInterval> | null = null;  // 1s Countdown-Updates
let tickTimer: ReturnType<typeof setTimeout> | null = null;       // nächster Tick (setTimeout)
let visibilityHandler: (() => void) | null = null;                // Page-Visibility-Listener
let headerResizeHandler: (() => void) | null = null;               // Header-Höhen-Sync bei Resize/Orientation
let currentActiveJob: ActiveJobTickState | null = null;

// NT-PFAND-SCHICHT-01: Hinweis „SAMMELT FLASCHEN · WAGEN x/30" auf der laufenden
// Flaschensammler-Karte — nur gesetzt, solange der Einkaufswagen Job-Fahrzeug ist.
interface PfandSchichtStatus {
  active: boolean;
  vehicleQty: number;
  vehicleCapacityUnits: number;
}
let pfandSchichtStatus: PfandSchichtStatus | null = null;
let isCompletingJob = false;  // Guard: verhindert doppelten Schicht-Abschluss
let isRecapModalOpen = false; // Guard: verhindert Job-Auto-Complete während Offline-Recap offen
let allJobs: JobStatusItem[] = [];
let cooldowns: { jobId: string; cooldownEndsAt: string }[] = [];
let queue: QueueEntry[] = [];
let queueMaxSlots = 0;
let selectedPhase = 1;
let selectedTab: Tab = 'jobs';
// NT-PFAND-UI-JOBFENSTER-01: View-Wechsel innerhalb des Jobs-Tabs (Liste ↔ Pfand-Betrieb-Panel).
// NT-WERKSTATT-BETRIEB-02B: 'werkstatt' als dritte View nach demselben Muster ergänzt.
// NT-WERKSTATT-CRAFT-02: 'werkstattCraft' als Sub-View von 'werkstatt' ("Zurück" führt zu
// 'werkstatt' zurück, nicht zu 'list').
// NT-LAYOUT-01 (L5): Jede Unteransicht hat seit jetzt eine eigene URL (JOBS_VIEW_PATHS) — die
// URL ist die Wahrheit, jobsView folgt ihr in renderDashboard(). Damit funktioniert
// Browser-Zurück, statt das Dashboard zu verlassen.
let jobsView: JobsView = 'list';
let lastRenderedTab: Tab | null = null; // Scroll-Reset-Tracking: wenn Tab wechselt → Scroll auf 0
const recentEvents: string[] = [];
let talentTree: TalentTreeData | null = null;
let talentTreeLoadFailed = false;
let learningStatus: LearningStatusResponse | null = null;
// Persistiert welche Zweige im Accordion offen sind (überlebt renderAll())
const openBranches = new Set<string>();
let learningPollingTimer: ReturnType<typeof setInterval> | null = null;
let debtStatus: DebtStatus | null = null;
let kladdeState: KladdeState | null = null;

// ─── Navigations-Konfiguration ────────────────────────────────────────────────

const NAV_ITEMS: readonly { id: Tab; label: string; group: string; available: boolean }[] = [
  { id: 'jobs',                 label: 'JOBS',        group: 'KARRIERE',   available: true  },
  { id: 'talents',              label: 'TALENTBAUM',  group: 'KARRIERE',   available: true  },
  { id: 'profile',              label: 'PROFIL',      group: 'KARRIERE',   available: true  },
  { id: 'items',                label: 'ITEMS',       group: 'KARRIERE',   available: false },
  { id: 'business_markt',       label: 'MARKT',       group: 'BUSINESS',   available: true  },
  { id: 'business_boerse',      label: 'BÖRSE',       group: 'BUSINESS',   available: true  },
  { id: 'warehouse',            label: 'LAGERHAUS',   group: 'BUSINESS',   available: true  },
  { id: 'crime',                label: 'CRIME OPS',   group: 'BUSINESS',   available: true  },
  { id: 'immobilien',           label: 'GESCHÄFTE',   group: 'IMMOBILIEN', available: true  },
  { id: 'immobilien_lager',     label: 'LAGER',       group: 'IMMOBILIEN', available: true  },
  { id: 'immobilien_verstecke', label: 'VERSTECKE',   group: 'IMMOBILIEN', available: true  },
  { id: 'stream',               label: 'STREAM',      group: 'COMMUNITY',  available: true  },
  { id: 'market',               label: 'MARKTPLATZ',  group: 'COMMUNITY',  available: false },
  { id: 'community',            label: 'LEADERBOARD', group: 'COMMUNITY',  available: true  },
  { id: 'org',                  label: 'ORGANISATION', group: 'ORGANISATION', available: true },
] as const;

const NAV_GROUPS = ['KARRIERE', 'BUSINESS', 'IMMOBILIEN', 'COMMUNITY', 'ORGANISATION'] as const;

// Pfad → Tab (für URL-basierte Tab-Auswahl beim Mount und nach Navigation)
const PATH_TO_TAB: Record<string, Tab> = {
  '/dashboard':              'jobs',
  '/jobs':                   'jobs',
  '/talentbaum':             'talents',
  '/profile':                'profile',
  '/business/markt':         'business_markt',
  '/business/boerse':        'business_boerse',
  '/business/lagerhaus':     'warehouse',
  '/business/crime-ops':     'crime',
  '/immobilien/geschaefte':  'immobilien',
  '/immobilien/lager':       'immobilien_lager',
  '/immobilien/verstecke':   'immobilien_verstecke',
  '/community/stream':       'stream',
  '/community/marktplatz':   'market',
  '/community/leaderboard':  'community',
  '/organisation':           'org',
  '/kladde':                 'kladde',
  '/brett':                  'brett',
};

/**
 * Pfad → Tab inkl. der Unterpfade aus NT-LAYOUT-01: Jobs-Unteransichten (/jobs/werkstatt …)
 * gehören zum Jobs-Tab, die eigenen Crime-Ops-Sektions-Pfade (/labor …) zum Crime-Ops-Tab.
 */
function tabForPath(path: string): Tab {
  if (getJobsViewForPath(path) !== null) return 'jobs';
  if (isCrimeOpsPath(path)) return 'crime';
  return PATH_TO_TAB[path] ?? 'jobs';
}

// Tab → Pfad (für Nav-Links und URL-Sync)
const TAB_TO_PATH: Partial<Record<Tab, string>> = {
  jobs:                   '/jobs',
  talents:                '/talentbaum',
  profile:                '/profile',
  business_markt:         '/business/markt',
  business_boerse:        '/business/boerse',
  warehouse:              '/business/lagerhaus',
  crime:                  '/business/crime-ops',
  immobilien:             '/immobilien/geschaefte',
  immobilien_lager:       '/immobilien/lager',
  immobilien_verstecke:   '/immobilien/verstecke',
  stream:                 '/community/stream',
  market:                 '/community/marktplatz',
  community:              '/community/leaderboard',
  org:                    '/organisation',
  kladde:                 '/kladde',
  brett:                  '/brett',
};

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

/** XP-Kurve (C=25, Aufgabe 2: PHASE2_XP_CURVE_AND_BUGFIX.md) */
function xpNeeded(level: number): number {
  return Math.floor(25 * Math.pow(level, 1.7));
}

/** Cent-String (BigInt-Serialisierung) in lesbaren Euro-Betrag umwandeln */
function formatCents(centStr: string): string {
  const val = parseFloat(centStr) / 100;
  return val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Formatiert einen Cent-Betrag MIT Vorzeichen für Fälle, in denen der Wert negativ sein kann
 * (z.B. Event-Boni aus BONUS_CENTS-Effekten, die auch Strafen sein können). `formatCents()` gibt
 * bei negativen Werten bereits ein führendes "-" zurück — ein hart codiertes "+" davor ergäbe
 * "+-3,00 ₦" statt "-3,00 ₦" (Bugfix: Spieler-Feedback zu unklaren Job-Abrechnungen).
 */
function formatSignedCents(centStr: string): { text: string; colorClass: 'val-green' | 'val-red' } {
  const isNegative = centStr.startsWith('-');
  const magnitude = isNegative ? centStr.slice(1) : centStr;
  return {
    text: `${isNegative ? '-' : '+'}${formatCents(magnitude)}`,
    colorClass: isNegative ? 'val-red' : 'val-green',
  };
}

/** Fahndungslevel als Punkt-Anzeige darstellen (z.B. Lv 3 → ●●●○○) */
function wantedDots(level: number): string {
  return Array.from({ length: 5 }, (_, i) => (i < level ? '●' : '○')).join('');
}

/** Prüft ob das Dashboard noch im DOM gemountet ist */
function isMounted(): boolean {
  return document.getElementById('dashboard-root') !== null;
}

/**
 * Hält `--header-height-mobile-actual` synchron mit der tatsächlich gerenderten Höhe des
 * zweizeiligen Mobile-Headers (Brand/Level/Wanted-Zeile + Economy-Zeile). Ein statischer
 * Konstantenwert kann das nicht zuverlässig abbilden: die Economy-Zeile zeigt
 * NOX/Schwarzgeld/Scheine, deren Ziffernlänge über die gesamte Spieldauer wächst — bei genug
 * Stellen bricht die zweite Zeile um oder wird höher, ohne dass sich am Layout sonst etwas
 * ändert (führte zu abgeschnittenem Tab-Inhalt oben, z. B. auf JOBS/RANGLISTE). Betroffen sind
 * `.dash-body`s margin-top/height (dashboard.css) und das Notification-Bell-Panel
 * (NotificationBell.ts) — beide lesen dieselbe neue Custom Property mit Fallback auf die
 * statische `--header-height-mobile` (design-tokens.css), solange JS noch nicht gemessen hat.
 * Bewusst NICHT `--header-height-mobile` selbst überschrieben: `.dash-header`s eigene
 * `min-height`-Regel liest diese Konstante unconditional (auch >= 768px, wo `height` sie sonst
 * überschreiben würde) — ein hier gemessener Mobile-Wert würde nach einem Resize auf Desktop-
 * Breite den Header dort fälschlich zu hoch machen.
 */
function syncHeaderHeightVar(): void {
  const header = document.querySelector<HTMLElement>('.dash-header');
  if (!header) return;
  const safeAreaTop = parseFloat(getComputedStyle(header).paddingTop) || 0;
  const contentHeight = Math.max(0, header.offsetHeight - safeAreaTop);
  document.documentElement.style.setProperty('--header-height-mobile-actual', `${String(contentHeight)}px`);
}

/** Timer aufräumen, State zurücksetzen */
function cleanup(): void {
  if (pollingTimer !== null) { clearInterval(pollingTimer); pollingTimer = null; }
  if (progressTimer !== null) { clearInterval(progressTimer); progressTimer = null; }
  if (tickTimer !== null) { clearTimeout(tickTimer); tickTimer = null; }
  if (learningPollingTimer !== null) { clearInterval(learningPollingTimer); learningPollingTimer = null; }
  openBranches.clear();
  debtStatus = null;
  kladdeState = null;
  pfandSchichtStatus = null;
  removePfandModal();
  resetPfandBetriebViewState();
  resetWerkstattViewState();
  resetWerkstattCraftViewState();
  resetBrettTab();
  jobsView = 'list';
  lastRenderedTab = null;
  if (visibilityHandler !== null) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (headerResizeHandler !== null) {
    window.removeEventListener('resize', headerResizeHandler);
    headerResizeHandler = null;
  }
}

/** Aktive Jobs-Unteransicht verlassen (State der View zurücksetzen). */
function leaveJobsView(): void {
  if (jobsView === 'pfandBetrieb') resetPfandBetriebViewState();
  if (jobsView === 'werkstatt') resetWerkstattViewState();
  if (jobsView === 'werkstattCraft') resetWerkstattCraftViewState();
  jobsView = 'list';
}

/** Jobs-Unteransicht betreten (Lade-State der View vorbereiten). */
function enterJobsView(view: JobsView): void {
  jobsView = view;
  if (view === 'pfandBetrieb') preparePfandBetriebView();
  if (view === 'werkstatt') prepareWerkstattView();
  if (view === 'werkstattCraft') prepareWerkstattCraftView();
}

/**
 * Räumt den aktuell aktiven Tab auf, bevor ein anderer gezeigt wird. Einzige Stelle dafür —
 * vorher dupliziert im Tab-Klick-Handler und in renderDashboard().
 */
function leaveCurrentTab(): void {
  if (selectedTab === 'jobs') leaveJobsView();
  if (selectedTab === 'community') resetCommunityState();
  if (selectedTab === 'stream') cleanupStreamTab();
  if (selectedTab === 'business_markt') cleanupMarkt();
  if (selectedTab === 'business_boerse') cleanupBoerse();
  if (selectedTab === 'warehouse') cleanupLagerhaus();
  if (selectedTab === 'immobilien') cleanupImmobilien();
  if (selectedTab === 'immobilien_lager') cleanupLager();
  if (selectedTab === 'immobilien_verstecke') cleanupVerstecke();
  if (selectedTab === 'crime') cleanupCrimeOps();
  if (selectedTab === 'org') cleanupOrgTab();
  if (selectedTab === 'brett') resetBrettTab();
}

/** Bereitet einen Tab vor, bevor er gezeigt wird. `path` bestimmt ggf. die Unteransicht. */
function enterTab(tab: Tab, path: string): void {
  if (tab === 'talents') openBranches.clear();
  if (tab === 'jobs') enterJobsView(getJobsViewForPath(path) ?? 'list');
  if (tab === 'kladde') prepareKladdeTab();
  if (tab === 'brett') prepareBrettTab();
  // 'crime': mountCrimeOpsTab() liest die Sektion selbst aus dem Pfad
}

/** Fortschritt einer Schicht als Prozent (0–100) */
function calcShiftProgress(startedAt: string, shiftEndsAt: string): number {
  const total = new Date(shiftEndsAt).getTime() - new Date(startedAt).getTime();
  const elapsed = Date.now() - new Date(startedAt).getTime();
  if (total <= 0) return 100;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

/** Mindest-Schichtfortschritt für Stop-Button – identisch zu Backend job.constants.ts */
const MIN_SHIFT_COMPLETION = 0.5;
const EARLY_ABORT_THRESHOLD_PERCENT = 0.2;
const EARLY_ABORT_MAX_JOB_LEVEL = 5;
const EARLY_ABORT_PHASES: readonly number[] = [1, 2];
function getRequiredShiftCompletion(jobPhase: number, jobLevel: number): number {
  const canAbortEarly = EARLY_ABORT_PHASES.includes(jobPhase) && jobLevel < EARLY_ABORT_MAX_JOB_LEVEL;
  return canAbortEarly ? EARLY_ABORT_THRESHOLD_PERCENT : MIN_SHIFT_COMPLETION;
}

/** Job-XP für nächstes Level – phasenbasiert, identisch zu Backend job.constants.ts */
const JOB_XP_BASE_PER_PHASE: Record<number, number> = { 1: 26, 2: 39, 3: 100, 4: 431, 5: 1100 };
function jobXpNeeded(level: number, phase: number): number {
  const base = JOB_XP_BASE_PER_PHASE[phase] ?? 26;
  return Math.floor(base * Math.pow(level, 1.5));
}

/** XP-Fortschrittsleiste + Label fuer Job-Level-Anzeige */
function jobXpBar(jobLevel: number, jobXp: number, jobPhase: number): string {
  const needed = jobXpNeeded(jobLevel, jobPhase);
  const pct = Math.min(100, Math.floor((jobXp / needed) * 100));
  return `
    <div class="job-level-bar" title="Job-Level ${String(jobLevel)} – ${String(jobXp)} / ${String(needed)} XP">
      <div class="job-level-fill" style="width:${String(pct)}%"></div>
    </div>
    <div class="job-xp-label">JOB-XP: ${String(jobXp)} / ${String(needed)}</div>
  `;
}

/** Verbleibende Zeit bis targetDate als MM:SS formatieren */
function formatCountdown(targetDateStr: string): string {
  const ms = Math.max(0, new Date(targetDateStr).getTime() - Date.now());
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Verbleibende Lernzeit formatieren:
 * < 1h   → MM:SS
 * 1h–24h → Xh YMin
 * > 24h  → X Stunden
 */
function formatLearnCountdown(completesAtStr: string): string {
  const ms = Math.max(0, new Date(completesAtStr).getTime() - Date.now());
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours >= 24) {
    return `${String(Math.ceil(ms / 3_600_000))} Stunden`;
  }
  if (hours >= 1) {
    return `${String(hours)}h ${String(minutes)}Min`;
  }
  return `${String(Math.floor(totalSec / 60)).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Lernzeit-Label aus Punkt-Kosten eines Talents ableiten */
function getTalentLearnTimeLabel(pointCost: number): string {
  if (pointCost >= 5) return '8 Stunden';
  if (pointCost >= 2) return '2 Stunden';
  return '30 Min';
}

/** HTML-Sonderzeichen escapen (XSS-Schutz für User-Content) */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Arabische Zahl zu Römisch (1–5) */
function roman(n: number): string {
  const numerals = ['I', 'II', 'III', 'IV', 'V'];
  return numerals[n - 1] ?? String(n);
}

// ─── Render: Header ───────────────────────────────────────────────────────────

function renderHeader(player: PlayerSnapshot): string {
  const xpVal = parseInt(player.xp, 10);
  const xpMax = xpNeeded(player.level);
  const xpPct = Math.min(100, Math.floor((xpVal / xpMax) * 100));

  return `
    <header class="dash-header">
      <span class="dash-brand">&#9654; NIGHTTRADE</span>
      <div class="dash-player-block">
        <a href="/profile" class="dash-username dash-username--link dash-header-username">${esc(player.username)}</a>
        ${renderPlayerBadges(
          {
            isAlphaTester: player.isAlphaTester,
            hasFrueherVogelBadge: player.hasFrueherVogelBadge,
            crimeRankColorVar: player.crimeRankColorVar,
            crimeRankLabel: player.crimeRankLabel,
            prestigeRank: player.prestigeRank,
          },
          'dash-header-badge',
        )}
        <span class="dash-level">LVL <span class="val-gold">${String(player.level)}</span></span>
        <div class="dash-xp-bar" title="${xpVal.toLocaleString('de-DE')} / ${xpMax.toLocaleString('de-DE')} XP">
          <div class="dash-xp-fill" style="width:${String(xpPct)}%"></div>
        </div>
      </div>
      <div class="dash-economy-block">
        <span class="val-gold" id="header-nox-balance" title="Bargeld">${formatCents(player.noxBalance)}&nbsp;&#8358;</span>
        <div class="dash-eco-row2">
          <span class="val-bm" id="header-black-money" title="Schwarzgeld">${formatCents(player.blackMoney)}&nbsp;S-&#8358;</span>
          <span class="dash-economy-sep">|</span>
          <a href="/shop" class="dash-scheine-link" title="Zum Shop">
            <span class="dash-scheine-label">SCHEINE</span>
            <span class="val-scheine" id="header-scheine" title="Scheine (Premium-Währung)">${String(player.scheine ?? 0)}</span>
          </a>
        </div>
      </div>
      <button class="dash-nav-action" id="dash-logout-btn">LOGOUT</button>
      ${renderNotificationBellButton()}
      <button class="dash-hamburger" id="dash-hamburger" type="button" aria-label="Menü öffnen">${HAMBURGER_ICON_SVG}</button>
      <span class="dash-header-break" aria-hidden="true"></span>
    </header>
  `;
}

// ─── Render: Navigation ───────────────────────────────────────────────────────

function renderNav(): string {
  const currentPath = window.location.pathname;
  return `
    <nav class="dash-nav" id="dash-nav">
      ${NAV_GROUPS.map((group) => `
        <div class="dash-nav-group">
          <p class="dash-nav-group-label">${group}</p>
          ${NAV_ITEMS
            .filter((item) => item.group === group)
            .map((item) => {
              const href = TAB_TO_PATH[item.id] ?? '#';
              // Unterpfade (/jobs/werkstatt, /labor …) markieren ihren Eltern-Eintrag als aktiv
              const isActive = item.available && (
                currentPath === href ||
                (item.id === 'jobs' && getJobsViewForPath(currentPath) !== null) ||
                (item.id === 'crime' && isCrimeOpsPath(currentPath))
              );
              const isCrimeOps = item.id === 'crime';
              return `
                <a
                  class="dash-nav-item${isActive ? ' is-active' : ''}${!item.available ? ' is-locked' : ''}${isCrimeOps ? ' is-crime-ops' : ''}"
                  href="${item.available ? href : '#'}"
                  ${!item.available ? 'aria-disabled="true"' : ''}
                >${esc(item.label)}</a>
              `;
            }).join('')}
        </div>
      `).join('')}
      <div class="dash-nav-settings">
        <a
          class="dash-nav-item dash-nav-item--kladde${currentPath === '/kladde' ? ' is-active' : ''}"
          href="/kladde"
          title="Die Kladde"
        >KLADDE</a>
        <a
          class="dash-nav-item dash-nav-item--brett${currentPath === '/brett' ? ' is-active' : ''}"
          href="/brett"
          title="Das Schwarze Brett"
        >BRETT</a>
        <a
          class="dash-nav-item dash-nav-item--settings${currentPath === '/settings' ? ' is-active' : ''}"
          href="/settings"
          title="Optionen"
        >&#9881; OPTIONEN</a>
      </div>
    </nav>
  `;
}

// ─── Render: Rechte Sidebar ───────────────────────────────────────────────────

function renderSidebar(player: PlayerSnapshot): string {
  const xpVal   = parseInt(player.xp, 10);
  const xpMax   = xpNeeded(player.level);
  const xpPct   = Math.min(100, Math.floor((xpVal / xpMax) * 100));
  const wanted  = player.wantedLevel;
  const wantedColor = wanted >= 4 ? 'var(--accent-red)'
    : wanted >= 2 ? 'var(--accent-orange)'
    : wanted >= 1 ? 'var(--accent-gold)'
    : 'var(--text-muted)';

  // Rank-Badge
  const rankLabel = player.crimeRankLabel ?? 'Normalbürger';
  // Farbe kommt vom Server (crimeRankColorVar, aus der Rang-Position berechnet) — keine feste
  // Key-Liste mehr, sonst fallen neue/umbenannte Ränge auf eine falsche Farbe zurück.
  const rankColor = player.crimeRankColorVar ? `var(${player.crimeRankColorVar})` : 'var(--text-muted)';

  return `
    <aside class="dash-sidebar" id="dash-sidebar">

      <!-- ── CHARAKTER ─────────────────────────────────── -->
      <section class="sidebar-section sidebar-section--charakter">
        <h3>CHARAKTER</h3>

        <div class="sidebar-stat sidebar-stat--large">
          <span>LEVEL</span>
          <span class="val-gold" id="sidebar-level">${String(player.level)}</span>
        </div>

        <div class="sidebar-xp-label">
          <span>${xpVal.toLocaleString('de-DE')} XP</span>
          <span style="color:var(--accent-gold);">${String(xpPct)}%</span>
        </div>
        <div class="sidebar-xp-bar">
          <div class="sidebar-xp-fill" style="width:${String(xpPct)}%"></div>
        </div>
        <div class="sidebar-xp-label" style="margin-top:2px;margin-bottom:var(--space-3);">
          <span>noch ${(xpMax - xpVal).toLocaleString('de-DE')} XP</span>
          <span>bis Level ${String(player.level + 1)}</span>
        </div>

        <div class="sidebar-stat">
          <span>RUF</span>
          <span class="val-green" id="sidebar-reputation">${String(player.reputation ?? 0)}&nbsp;/&nbsp;1000</span>
        </div>

        <div class="sidebar-stat">
          <span>RANG</span>
          <span style="color:${rankColor};font-size:13px;letter-spacing:0.04em;">${esc(rankLabel)}</span>
        </div>

        ${(player.prestigeRank ?? 0) > 0 ? `
        <div class="sidebar-stat">
          <span>PRESTIGE</span>
          <span style="color:var(${getPrestigeColorVar(player.prestigeRank ?? 1)});">${['I', 'II', 'III'][(player.prestigeRank ?? 1) - 1] ?? String(player.prestigeRank)}</span>
        </div>` : ''}
      </section>

      <!-- ── FAHNDUNG ───────────────────────────────────── -->
      <section class="sidebar-section sidebar-section--fahndung">
        <h3>FAHNDUNG</h3>
        <button class="sidebar-wanted-btn" id="sidebar-wanted-btn" data-wanted-toggle="sidebar-wanted-detail" data-wanted-arrow="sidebar-wanted-arrow" title="Klicken für Details">
          <span class="sidebar-wanted-dots-row">
            ${wantedDots(wanted)}
          </span>
          <span style="font-size:14px;letter-spacing:0.06em;color:${wantedColor};font-weight:bold;">${getWantedLevelLabel(wanted)}</span>
          <span style="font-size:11px;color:var(--text-muted);margin-left:auto;" id="sidebar-wanted-arrow">▼</span>
        </button>
        ${renderWantedDetailRows(player, 'sidebar-wanted-detail')}
      </section>

      <!-- ── KONTO ──────────────────────────────────────── -->
      <section class="sidebar-section sidebar-section--konto">
        <h3>KONTO</h3>
        <div class="sidebar-money-row">
          <span class="sidebar-money-value val-gold" id="sidebar-balance">${formatCents(player.noxBalance)}&nbsp;&#8358;</span>
        </div>
        <div class="sidebar-money-row">
          <span class="sidebar-money-value val-bm" id="sidebar-black-money">${formatCents(player.blackMoney)}&nbsp;S-&#8358;</span>
        </div>
        ${(player.scheine ?? 0) > 0 ? `
        <div class="sidebar-money-row">
          <span class="sidebar-money-value val-scheine">${String(player.scheine ?? 0)}&nbsp;Scheine</span>
        </div>` : ''}
      </section>

      <!-- ── EVENT-LOG ──────────────────────────────────── -->
      <section class="sidebar-section sidebar-section--events">
        <h3>EVENT-LOG</h3>
        <div class="event-log" id="dash-event-log">
          ${recentEvents.length === 0
            ? '<p class="dash-empty">Keine Events.</p>'
            : recentEvents.map((e) => `<p class="event-log-entry">${esc(e)}</p>`).join('')}
        </div>
      </section>

    </aside>
  `;
}

function renderWantedDetailRows(player: PlayerSnapshot, detailId: string): string {
  const wanted = player.wantedLevel;
  const wantedPts = player.wantedPoints ?? 0;
  const nextThreshold = getNextWantedThreshold(wanted);
  const MAX_WANTED_POINTS = 600;
  const clearAt = 24; // Punkte unter 25 = SAUBER
  const wantedColor = wanted >= 4 ? 'var(--accent-red)'
    : wanted >= 2 ? 'var(--accent-orange)'
    : wanted >= 1 ? 'var(--accent-gold)'
    : 'var(--text-muted)';

  return `
    <div class="sidebar-wanted-detail" id="${detailId}" style="display:none;">
      <div class="sidebar-detail-row">
        <span>Punkte</span>
        <span style="color:${wantedColor};">${String(wantedPts)} / ${String(MAX_WANTED_POINTS)}</span>
      </div>
      ${nextThreshold !== null ? `<div class="sidebar-detail-row"><span>${getWantedLevelLabel(wanted + 1)} ab</span><span>+${String(nextThreshold - wantedPts)} P</span></div>` : `<div class="sidebar-detail-row"><span>Puffer bis Max</span><span style="color:var(--accent-red);">${String(Math.max(0, 600 - wantedPts))} P</span></div>`}
      ${wantedPts > 0 ? `<div class="sidebar-detail-row"><span>Abbau</span><span>~${String(wantedPts)} Min (-1P/Min)</span></div>` : ''}
      ${wanted >= 1 && wantedPts < 100 ? `<div class="sidebar-detail-row"><span>SAUBER bei</span><span style="color:var(--accent-green);">< ${String(clearAt + 1)} P</span></div>` : ''}
      ${wanted === 0 && wantedPts === 0 ? `<div class="sidebar-detail-row" style="color:var(--accent-green);">SAUBER</div>` : ''}
    </div>
  `;
}

// ─── Render: Jobs-Tab ─────────────────────────────────────────────────────────

/**
 * NT-PFAND-UI-JOBFENSTER-01: Betriebs-Panel als View-Wechsel im Content-Bereich statt Modal —
 * App-Shell (Header/Nav/Sidebar) bleibt stehen, nur dieser Bereich tauscht Liste ↔ Panel.
 */
function renderJobsPfandBetriebView(): string {
  return `
    <section class="dash-content" id="dash-jobs">
      <div class="dash-content-header dash-content-header--jobs-view-back">
        <button class="btn-jobs-view-back" type="button" data-pfand-betrieb-back>&larr; ZUR&Uuml;CK</button>
        <h2>PFAND-IMPERIUM</h2>
      </div>
      ${renderPfandBetriebViewShell()}
    </section>
  `;
}

/** NT-WERKSTATT-BETRIEB-02B: dieselbe View-Wechsel-Mechanik wie beim Pfand-Betrieb-Panel. */
function renderJobsWerkstattView(): string {
  return `
    <section class="dash-content" id="dash-jobs">
      <div class="dash-content-header dash-content-header--jobs-view-back">
        <button class="btn-jobs-view-back" type="button" data-werkstatt-back>&larr; ZUR&Uuml;CK</button>
        <h2>WERKSTATT</h2>
      </div>
      ${renderWerkstattViewShell()}
    </section>
  `;
}

/** NT-WERKSTATT-CRAFT-02: Sub-View der Werkstatt, "Zurück" führt zu 'werkstatt' zurück. */
function renderJobsWerkstattCraftView(): string {
  return `
    <section class="dash-content" id="dash-jobs">
      <div class="dash-content-header dash-content-header--jobs-view-back">
        <button class="btn-jobs-view-back" type="button" data-werkstatt-craft-back>&larr; ZUR&Uuml;CK</button>
        <h2>TEILE CRAFTEN</h2>
      </div>
      ${renderWerkstattCraftViewShell()}
    </section>
  `;
}

function renderJobsTab(): string {
  if (jobsView === 'pfandBetrieb') return renderJobsPfandBetriebView();
  if (jobsView === 'werkstatt') return renderJobsWerkstattView();
  if (jobsView === 'werkstattCraft') return renderJobsWerkstattCraftView();

  const filteredJobs = allJobs.filter((j) => j.phase === selectedPhase && !j.playerState.isHidden);

  return `
    <section class="dash-content" id="dash-jobs">
      ${kladdeState ? renderKladdePanel(kladdeState) : ''}
      <div class="dash-content-header">
        <h2>JOBS</h2>
        <div class="phase-buttons">
          ${[1, 2, 3, 4, 5].map((p) => {
            const PHASE_LABELS: Record<number, string> = {
              1: 'DER TIEFPUNKT',
              2: 'HALBSEIDENE GESCH\u00c4FTE',
              3: 'AUF EIGENE RECHNUNG',
              4: 'DER MITTELSTAND',
              5: 'UNANTASTBAR',
            };
            const phaseJobs = allJobs.filter((j) => j.phase === p);
            const hasAnyJob = phaseJobs.length > 0;
            const allLockedForPlayer = hasAnyJob && phaseJobs.every((j) => !j.playerState.isUnlocked);
            const isLocked = !hasAnyJob || allLockedForPlayer;
            const minLevel = hasAnyJob ? Math.min(...phaseJobs.map((j) => j.unlockLevel)) : null;
            const lockedMsg = !hasAnyJob
              ? `${PHASE_LABELS[p] ?? `Phase ${roman(p)}`}: Noch nicht verfügbar`
              : `Ab Level ${String(minLevel)} verfügbar`;
            const phaseLabel = PHASE_LABELS[p] ?? `PHASE ${roman(p)}`;
            return `
              <button
                class="phase-btn phase-btn--p${String(p)}${selectedPhase === p ? ' is-active' : ''}${isLocked ? ' is-locked' : ''}${p === 5 ? ' phase-btn--kartellchef' : ''}"
                data-phase="${String(p)}"
                ${isLocked ? `data-locked-msg="${lockedMsg}"` : ''}
                title="${isLocked ? lockedMsg : phaseLabel}"
              >${phaseLabel}</button>
            `;
          }).join('')}
        </div>
      </div>
      ${renderBetriebeShortcuts()}

      <div class="job-grid" id="job-grid">
        ${filteredJobs.length === 0
          ? `<p class="dash-empty">Keine Jobs verfügbar.</p>`
          : filteredJobs.map(renderJobCard).join('')}
      </div>

      ${queueMaxSlots > 0 ? renderQueuePanel() : ''}
    </section>
  `;
}

/**
 * NT-PFAND-SAMMLER-01: FLASCHEN-Zeile + Abgabe-Button für den Flaschensammler.
 * Immer klickbar (auch während laufender Schicht, auch bei Bestand 0) — daher in allen drei
 * "freigeschalteten" Job-Card-Zuständen eingebunden (aktiv/cooldown/verfügbar), aber NICHT im
 * GESPERRT-Zustand (vor dem ersten Freischalten gibt es noch keinen Pfand-Bestand).
 * Kein Live-Bestandszähler auf der Karte mehr (Chris-Feedback: "0/xxx" war verwirrend) — der
 * tatsächliche Bestand wird beim Öffnen von PfandModal.ts ohnehin frisch geladen.
 */
function renderPfandRow(job: JobStatusItem): string {
  if (job.id !== 'flaschensammler') return '';
  return `
    <div class="job-card-pfand-row">
      <span class="job-card-pfand-label">FLASCHEN</span>
      <button class="btn-pfand-abgeben" type="button" data-pfand-open>FLASCHEN ABGEBEN</button>
    </div>
  `;
}

/** Text des Sammel-Hinweises — leer, wenn kein Einkaufswagen gesetzt ist. */
function pfandSchichtHintText(): string {
  if (pfandSchichtStatus?.active !== true) return '';
  return `SAMMELT FLASCHEN · WAGEN ${String(pfandSchichtStatus.vehicleQty)}/${String(pfandSchichtStatus.vehicleCapacityUnits)}`;
}

/**
 * NT-PFAND-SCHICHT-01: dezenter Hinweis nur auf der LAUFENDEN Flaschensammler-Karte. Das Element
 * wird immer gerendert (stabile ID), damit syncPfandSchichtStatus() es per DOM-Patch füllen kann.
 */
function renderPfandSchichtHint(job: JobStatusItem): string {
  if (job.id !== 'flaschensammler') return '';
  const text = pfandSchichtHintText();
  return `<div class="job-card-pfand-hint" id="pfand-schicht-hint"${text === '' ? ' hidden' : ''}>${esc(text)}</div>`;
}

/** Lädt den Wagen-Füllstand, solange eine Flaschensammler-Schicht läuft. */
function syncPfandSchichtStatus(): void {
  if (currentActiveJob?.jobId !== 'flaschensammler') {
    pfandSchichtStatus = null;
    return;
  }
  void api.get<PfandSchichtStatus>('/pfand/schicht-status').then((data) => {
    if (!isMounted()) return;
    pfandSchichtStatus = data;
    const el = document.getElementById('pfand-schicht-hint');
    if (el === null) return;
    const text = pfandSchichtHintText();
    el.textContent = text;
    el.hidden = text === '';
  }).catch(() => { /* Nicht-kritisch: Hinweis bleibt einfach aus */ });
}

/**
 * NT-PFAND-IMPERIUM-01: Zugang zur Betriebsverwaltung (Reviere/Angestellte/Kasse) — additiv zur
 * Schicht, nicht als Ersatz. Nur gerendert, wenn die Job-Card überhaupt sichtbar ist (isHidden
 * filtert das bereits vorher aus der Jobs-Liste).
 */
function renderBetriebRow(job: JobStatusItem): string {
  if (job.id !== 'pfandflaschen_imperium') return '';
  return `
    <div class="job-card-pfand-row">
      <span class="job-card-pfand-label">BETRIEB</span>
      <button class="btn-pfand-abgeben" type="button" data-pfand-betrieb-open>VERWALTEN</button>
    </div>
  `;
}

/**
 * NT-WERKSTATT-BETRIEB-02B: Zugang zur Werkstatt-Verwaltungsseite (Mechaniker/Kunden/Puffer) —
 * additiv zur Schicht, nicht als Ersatz. Muster 1:1 renderBetriebRow(). Nur gerendert, wenn die
 * Job-Card überhaupt sichtbar ist (isHidden filtert das bereits vorher aus der Jobs-Liste).
 */
function renderWerkstattRow(job: JobStatusItem): string {
  if (job.id !== 'werkstatt') return '';
  return `
    <div class="job-card-pfand-row">
      <span class="job-card-pfand-label">WERKSTATT</span>
      <button class="btn-pfand-abgeben" type="button" data-werkstatt-open>VERWALTEN</button>
    </div>
  `;
}

/**
 * NT-JOBS-BETRIEBE-SHORTCUT-01: angepinnte Sektion oberhalb der Jobs-Liste mit Direkteinstiegen in
 * bereits freigeschaltete Nebengeschäfte (Flaschensammler/Pfandflaschen-Imperium/Werkstatt).
 * Leitet ihre Einträge ausschließlich aus dem bereits geladenen `allJobs` ab (derselbe Payload wie
 * für die Job-Karten selbst, aus `/jobs/status`) — kein neuer Freischaltzustand, kein zusätzlicher
 * Roundtrip. Ohne mindestens ein freigeschaltetes Nebengeschäft wird die Sektion komplett
 * weggelassen (kein leerer Kasten, kein Platzhalter — analog zum isHidden-Verhalten der Karten).
 * Auswahl-/Sortierlogik ist DOM-frei nach dashboard.betriebe-shortcuts.ts ausgelagert (Muster
 * crimeOps.categories.ts) — dort isoliert testbar, hier nur noch Markup.
 */
function renderBetriebeShortcuts(): string {
  const entries = selectVisibleBetriebeShortcuts(allJobs);
  if (entries.length === 0) return '';

  return `
    <section class="betriebe-shortcuts" id="betriebe-shortcuts">
      <span class="betriebe-shortcuts-label">BETRIEBE</span>
      <div class="betriebe-shortcuts-list">
        ${entries.map(({ job, dataAttr }) => `
          <button class="btn-pfand-abgeben betriebe-shortcut-btn" type="button" ${dataAttr}>${esc(job.name)}</button>
        `).join('')}
      </div>
    </section>
  `;
}

function renderJobCard(job: JobStatusItem): string {
  const ps = job.playerState;
  const isBlackMoney = job.rewardCurrency === 'BLACK_MONEY';
  const isHighRisk = job.wantedPointsDelta >= 1;
  const isPrestigeLocked = job.id === 'die_letzte_adresse'; // TODO Phase 8: echte Prestige-Pruefung

  // Hilfs-HTML: Verdienst-Anzeige je nach Waehrung
  // NT-JOB-LAEUFER-01: zusätzlicher, immer BLACK_MONEY Bonus-Betrag (rewardBlackCents) wird
  // neben der primären Belohnung angezeigt — 0 für jeden anderen Job, also unsichtbar.
  const rewardDisplay =
    (isBlackMoney
      ? `<span style="color:var(--accent-bm)">${(job.rewardCents / 100).toFixed(2)}&nbsp;S-&#8358;</span>`
      : `<span>${(job.rewardCents / 100).toFixed(2)}&nbsp;&#8358;</span>`) +
    (job.rewardBlackCents > 0
      ? ` <span style="color:var(--accent-bm)">+${(job.rewardBlackCents / 100).toFixed(2)}&nbsp;S-&#8358;</span>`
      : '');

  // Hilfs-HTML: Risiko-Warnung bei hohem Fahndungslevel-Anstieg
  const riskBadge = isHighRisk
    ? `<span class="job-risk-badge" title="Hohes Fahndungsrisiko (+${String(job.wantedPointsDelta)} Punkte)">!</span>`
    : '';

  // ── AKTIV: Schicht läuft ──────────────────────────────────────────────────
  if (currentActiveJob !== null && currentActiveJob.jobId === job.id) {
    const aj = currentActiveJob;
    const shiftPct = aj.shiftEndsAt !== null ? calcShiftProgress(aj.startedAt, aj.shiftEndsAt) : 0;
    return `
      <div class="job-card job-card--active" id="active-job-card-${esc(job.id)}">
        <div class="job-card-top">
          <span class="job-name">${esc(job.name)}${riskBadge}</span>
          <span class="job-badge job-badge--active">&#9654; AKTIV</span>
          ${ps.jobLevel > 1 ? `<span class="job-level">JOB-LVL ${String(ps.jobLevel)}</span>` : ''}
        </div>
        <p class="job-desc"><em>${esc(job.description)}</em></p>
        <div class="job-stats">
          ${rewardDisplay}
          <span>${String(job.rewardXp)}&nbsp;XP</span>
        </div>
        ${jobXpBar(ps.jobLevel, ps.jobXp, ps.jobPhase)}
        <div class="job-shift-info">
          <span class="job-shift-label">ENDET IN</span>
          <span class="val-green" id="shift-countdown-${esc(job.id)}">${aj.shiftEndsAt !== null ? formatCountdown(aj.shiftEndsAt) : '--:--'}</span>
        </div>
        <div class="job-progress-bar">
          <div class="job-progress-fill job-progress-fill--shift" id="shift-progress-${esc(job.id)}" style="--shift-progress:${String(Math.floor(shiftPct))}%"></div>
        </div>
        ${(() => {
          const shiftDurationMs = job.shiftDurationMinutes * 60 * 1000;
          const nowMs = Date.now();
          const elapsed = nowMs - new Date(aj.startedAt).getTime();
          const progress = Math.min(1, elapsed / shiftDurationMs);
          const shiftEnded = aj.shiftEndsAt !== null && nowMs >= new Date(aj.shiftEndsAt).getTime();
          // Abbruchgrenze basiert auf originaler Schichtdauer (entspricht Backend-Logik)
          const requiredCompletion = getRequiredShiftCompletion(job.phase, aj.jobLevel);
          const canStop = progress >= requiredCompletion;
          if (shiftEnded) {
            return `<button class="btn-complete btn-complete--ready" data-complete-job="${esc(job.id)}">SCHICHT ABSCHLIESSEN</button>`;
          } else if (canStop) {
            // Anzeigeprozentwert basiert auf tatsächlicher Schichtdauer (inkl. Zeitstrafen durch Events)
            const actualShiftEndMs = aj.shiftEndsAt !== null
              ? new Date(aj.shiftEndsAt).getTime()
              : nowMs + (shiftDurationMs - elapsed);
            const actualDurationMs = actualShiftEndMs - new Date(aj.startedAt).getTime();
            const pct = Math.floor(Math.min(1, elapsed / actualDurationMs) * 100);
            return `<button class="btn-complete" data-complete-job="${esc(job.id)}">&#9632; BEENDEN (${String(pct)}%)</button>`;
          } else {
            const halfwayMs = Math.max(0, shiftDurationMs * requiredCompletion - elapsed);
            const countdown = formatCountdown(new Date(nowMs + halfwayMs).toISOString());
            return `<button class="btn-complete btn-complete--locked" disabled data-complete-job="${esc(job.id)}">${countdown}</button>`;
          }
        })()
        }
        ${renderPfandSchichtHint(job)}
        ${renderPfandRow(job)}
        ${renderBetriebRow(job)}
        ${renderWerkstattRow(job)}
      </div>
    `;
  }

  // ── COOLDOWN: Schicht beendet, Cooldown läuft ─────────────────────────────
  if (ps.isInCooldown && ps.cooldownEndsAt !== null) {
    return `
      <div class="job-card job-card--cooldown">
        <div class="job-card-top">
          <span class="job-name">${esc(job.name)}${riskBadge}</span>
          <span class="job-badge job-badge--cooldown">COOLDOWN</span>
          ${ps.jobLevel > 1 ? `<span class="job-level">JOB-LVL ${String(ps.jobLevel)}</span>` : ''}
        </div>
        <p class="job-desc"><em>${esc(job.description)}</em></p>
        <div class="job-stats">
          ${rewardDisplay}
          <span>${String(job.rewardXp)}&nbsp;XP</span>
        </div>
        ${jobXpBar(ps.jobLevel, ps.jobXp, ps.jobPhase)}
        <div class="job-cooldown-info">
          <span class="job-cooldown-label">VERFÜGBAR IN</span>
          <span class="val-orange" id="cooldown-countdown-${esc(job.id)}">${formatCountdown(ps.cooldownEndsAt)}</span>
        </div>
        <div class="job-card-actions">
          ${queueMaxSlots > 0 && !queue.some((q) => q.jobId === job.id) ? `
            <button class="btn-queue-add" data-queue-add="${esc(job.id)}">IN QUEUE +</button>
          ` : ''}
        </div>
        ${renderPfandRow(job)}
        ${renderBetriebRow(job)}
        ${renderWerkstattRow(job)}
      </div>
    `;
  }

  // ── GESPERRT: Level zu niedrig ────────────────────────────────────────────
  if (!ps.isUnlocked) {
    return `
      <div class="job-card job-card--locked">
        <div class="job-card-top">
          <span class="job-name job-name--locked">${esc(job.name)}${riskBadge}</span>
          ${job.isCrimeJob ? '<span class="job-badge job-badge--crime">CRIME</span>' : ''}
        </div>
        <p class="job-desc"><em>${esc(job.description)}</em></p>
        <div class="job-stats">
          ${rewardDisplay}
          <span>${String(job.rewardXp)}&nbsp;XP</span>
          <span>Schicht: ${String(job.shiftDurationMinutes)}&nbsp;Min</span>
        </div>
        ${isPrestigeLocked
          ? `<button class="btn-prestige-locked" disabled style="color:var(--accent-gold);border-color:var(--accent-gold)">NUR PRESTIGE II+</button>`
          : `<button class="btn-locked" disabled>AB LEVEL ${String(job.unlockLevel)}</button>`
        }
      </div>
    `;
  }

  // ── GESPERRT: Talent fehlt (Level erfüllt, aber requiredTalents nicht gelernt) ────────────
  if (ps.missingTalentNames.length > 0) {
    return `
      <div class="job-card job-card--locked">
        <div class="job-card-top">
          <span class="job-name job-name--locked">${esc(job.name)}${riskBadge}</span>
          ${job.isCrimeJob ? '<span class="job-badge job-badge--crime">CRIME</span>' : ''}
        </div>
        <p class="job-desc"><em>${esc(job.description)}</em></p>
        <div class="job-stats">
          ${rewardDisplay}
          <span>${String(job.rewardXp)}&nbsp;XP</span>
          <span>Schicht: ${String(job.shiftDurationMinutes)}&nbsp;Min</span>
        </div>
        <p class="job-desc" style="color:var(--accent-purple)">
          Talent erforderlich: ${esc(ps.missingTalentNames.join(', '))}
        </p>
        <button class="btn-locked" disabled title="Im Talentbaum lernen">TALENT ERFORDERLICH</button>
      </div>
    `;
  }

  // ── VERFUEGBAR: startbar ─────────────────────────────────────────────────
  const canStart = currentActiveJob === null;
  const isInQueue = queue.some((q) => q.jobId === job.id);

  return `
    <div class="job-card${job.isCrimeJob ? ' job-card--crime' : ''}">
      <div class="job-card-top">
        <span class="job-name">${esc(job.name)}${riskBadge}</span>
        ${job.isCrimeJob ? '<span class="job-badge job-badge--crime">CRIME</span>' : ''}
        ${ps.jobLevel > 1 ? `<span class="job-level">JOB-LVL ${String(ps.jobLevel)}</span>` : ''}
        ${isInQueue ? '<span class="job-badge job-badge--queue">IN QUEUE</span>' : ''}
      </div>
      <p class="job-desc"><em>${esc(job.description)}</em></p>
      <div class="job-stats">
        ${rewardDisplay}
        <span>${String(job.rewardXp)}&nbsp;XP</span>
        <span>Schicht: ${String(job.shiftDurationMinutes)}&nbsp;Min</span>
      </div>
      ${jobXpBar(ps.jobLevel, ps.jobXp, ps.jobPhase)}
      <div class="job-card-actions">
        <button
          class="btn-start${job.isCrimeJob ? ' btn-start--crime' : ''}"
          data-start-job="${esc(job.id)}"
          ${!canStart ? 'disabled' : ''}
          title="${canStart ? esc(job.name) + ' starten' : 'Erst aktiven Job beenden'}"
        >&#9654; STARTEN</button>
        ${queueMaxSlots > 0 && !isInQueue ? `
          <button class="btn-queue-add" data-queue-add="${esc(job.id)}">IN QUEUE +</button>
        ` : ''}
      </div>
      ${renderPfandRow(job)}
      ${renderBetriebRow(job)}
      ${renderWerkstattRow(job)}
    </div>
  `;
}

function renderQueuePanel(): string {
  return `
    <section class="queue-panel" id="queue-panel">
      <div class="queue-panel-header">
        <h3>WARTESCHLANGE</h3>
        <span class="queue-slots">${String(queue.length)}&nbsp;/&nbsp;${String(queueMaxSlots)}</span>
      </div>
      ${queue.length === 0
        ? '<p class="dash-empty">Warteschlange leer.</p>'
        : `<div class="queue-list">
            ${[...queue]
              .sort((a, b) => a.position - b.position)
              .map((entry) => `
                <div class="queue-entry">
                  <span class="queue-pos">${String(entry.position)}.</span>
                  <span class="queue-job-name">${esc(entry.jobName)}</span>
                  <button class="queue-remove-btn" data-queue-remove="${esc(entry.id)}" title="Aus Queue entfernen">&#10005;</button>
                </div>
              `).join('')}
          </div>`}
    </section>
  `;
}

// ─── Render: Talent-Baum ─────────────────────────────────────────────────────

/** Schulden-Banner (oben im Talent-Tab, wenn Schulden vorhanden) */
function renderDebtBanner(): string {
  if (!debtStatus?.hasDebt) return '';
  const eurDisplay = formatCents(debtStatus.debtNox);
  const bmDisplay = formatCents(debtStatus.debtBlackMoney);
  return `
    <div class="talent-debt-banner">
      <div class="talent-debt-info">
        <span class="talent-debt-label">[SCHULDEN AKTIV]</span>
        <span class="talent-debt-amounts">₦: ${eurDisplay} | S-₦: ${bmDisplay}</span>
        <span class="talent-debt-debuff">-${String(debtStatus.incomeDebuffPercent)}% AUF ALLE EINNAHMEN</span>
      </div>
      <button class="talent-repay-btn" data-repay-debt>SCHULDEN TILGEN</button>
    </div>
  `;
}

/** Status-Panel für aktive Lernzeit (oben im Talent-Tab) */
function renderLearningStatusPanel(): string {
  if (learningStatus === null) return '';
  const { currentLearning: cl, completedButUncollected } = learningStatus;

  const uncollectedBadge = completedButUncollected.length > 0
    ? `<span class="talent-uncollected-badge">${String(completedButUncollected.length)} ABHOLBEREIT</span>`
    : '';

  if (cl === null) {
    return `
      <div class="talent-learning-panel">
        <div class="talent-learning-panel-header">
          <span class="talent-learning-panel-label">IN LERNZEIT</span>
          ${uncollectedBadge}
        </div>
        <p class="talent-learning-empty">Kein Talent in Lernzeit.</p>
      </div>
    `;
  }

  const pct = cl.progressPercent;
  const lernzeit = formatLearnCountdown(cl.completesAt);

  return `
    <div class="talent-learning-panel">
      <div class="talent-learning-panel-header">
        <span class="talent-learning-panel-label">IN LERNZEIT</span>
        ${uncollectedBadge}
      </div>
      <p class="talent-learning-name">${esc(cl.talentName.toUpperCase())}</p>
      <div class="talent-learn-progress-bar">
        <div class="talent-learn-progress-fill" id="talent-learning-progress" style="width:${String(pct)}%"></div>
      </div>
      <div class="talent-learning-meta-row">
        <span class="val-muted">Abgeschlossen in:</span>
        <span class="val-gold" id="talent-learning-countdown">${lernzeit}</span>
        <button class="talent-skip-btn" data-skip-talent="${esc(cl.talentId)}">${String(cl.skipCostScheine)} SCHEINE - SOFORT</button>
      </div>
    </div>
  `;
}

function renderTalentsTab(): string {
  if (talentTree === null) {
    const msg = talentTreeLoadFailed ? 'Keine Talente verfügbar.' : 'Lade Talentbaum...';
    return `
      <section class="dash-content" id="dash-talents">
        <div class="dash-content-header"><h2>TALENTBAUM</h2></div>
        <p class="dash-empty">${msg}</p>
      </section>
    `;
  }

  const { availableTalentPoints, branches } = talentTree;

  // Flache Map aller Talents für Prerequisite-Lookup
  const allNodes = new Map<string, TalentNode>(
    TALENT_BRANCH_KEYS.flatMap((branch) => branches[branch]).map((t): [string, TalentNode] => [t.id, t]),
  );

  return `
    <section class="dash-content" id="dash-talents">
      <div class="dash-content-header">
        <h2>TALENTBAUM</h2>
        <span class="talent-pts-badge">PUNKTE:&nbsp;<span class="val-gold">${String(availableTalentPoints)}</span></span>
      </div>
      <p class="dash-meta-hint">TALENTPUNKTE: 1 pro Profil-Level bis Level 10 · 1 alle 2 Job-Level in jedem Job</p>
      ${renderDebtBanner()}
      ${renderLearningStatusPanel()}
      <div class="talent-accordion">
        ${TALENT_BRANCH_KEYS.map((branch) => {
          const meta = TALENT_BRANCH_META[branch];
          const nodes = branches[branch];
          const invested = nodes.filter((t) => t.investedPoints > 0).length;
          const total = nodes.length;
          const isOpen = openBranches.has(branch);
          const hasTalents = total > 0;
          return `
            <div class="talent-accordion-item${isOpen ? ' talent-accordion-item--open' : ''}${!hasTalents ? ' talent-accordion-item--empty' : ''}" data-accordion-branch="${branch}">
              <button class="talent-accordion-header" data-toggle-branch="${branch}" type="button" ${!hasTalents ? 'disabled' : ''}>
                <span class="talent-accordion-icon">${meta.icon}</span>
                <span class="talent-accordion-name">${meta.label}</span>
                <span class="talent-accordion-info">
                  ${invested > 0 ? `<span class="talent-acc-invested">${String(invested)}/${String(total)} aktiv</span>` : `<span class="talent-acc-count">${String(total)} Talente</span>`}
                </span>
                <span class="talent-accordion-toggle">${isOpen ? '[-]' : '[+]'}</span>
              </button>
              <div class="talent-accordion-meta">
                <span class="talent-accordion-motto">"${meta.motto}"</span>
                <div class="talent-accordion-themes">
                  ${meta.themes.map((th) => `<span class="talent-theme-badge talent-theme-badge--${th.color}">${th.label}</span>`).join('')}
                </div>
              </div>
              <div class="talent-accordion-body">
                ${[1, 2, 3].map((tier) => {
                  const group = nodes.filter((t) => t.tier === tier);
                  if (group.length === 0) return '';
                  return `
                    <div class="talent-tier">
                      <span class="talent-tier-label">TIER&nbsp;${String(tier)}</span>
                      ${group.map((t) => renderTalentCard(t, availableTalentPoints, allNodes)).join('')}
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </section>
  `;
}

function renderTalentCard(
  t: TalentNode,
  availPts: number,
  allNodes: Map<string, TalentNode>,
): string {
  const cl = learningStatus?.currentLearning ?? null;
  const isUncollected = learningStatus?.completedButUncollected.some((u) => u.talentId === t.id) ?? false;
  const isLocked = !t.prerequisiteMet;

  // Phase-5-Sperre: Zweig gehört zum noch nicht gebauten Organisations-System.
  const isPhaseLocked = PHASE5_LOCKED_BRANCHES.includes(t.branch as TalentBranchKey);

  // Level-Gate: Spieler-Level zu niedrig?
  const player = gameState.getPlayer();
  const playerLevel = player?.level ?? 1;
  const isLevelLocked = t.minLevel !== undefined && playerLevel < t.minLevel;

  // Effekt-Text zusammenbauen
  const effectParts: string[] = [];
  if (t.effect.incomeMultiplier !== undefined) {
    effectParts.push(`+${String(Math.round(t.effect.incomeMultiplier * 100))}% Einkommen`);
  }
  if (t.effect.deceptionEventSuccessRate !== undefined) {
    effectParts.push(`+${String(Math.round(t.effect.deceptionEventSuccessRate * 100))}% Täuschung`);
  }
  if (t.effect.negotiationsUnlocked === true) {
    effectParts.push('Verhandlungen aktiv');
  }
  if (t.effect.dealBonus !== undefined) {
    effectParts.push(`+${String(Math.round(t.effect.dealBonus * 100))}% Deal-Bonus`);
  }
  if (t.effect.passiveReputationMultiplier !== undefined) {
    effectParts.push(`${String(t.effect.passiveReputationMultiplier)}x Ruf passiv`);
  }

  // Investitions-Punkte als Dots (z.B. ●●○ für 2/3)
  const dots = Array.from(
    { length: t.maxPoints },
    (_, i) => (i < t.investedPoints ? '&#9679;' : '&#9675;'),
  ).join('');

  // Prerequisite-Name aus allNodes auflösen
  const prerequisiteIds = [
    ...(t.prerequisiteId !== undefined ? [t.prerequisiteId] : []),
    ...(t.prerequisiteIds ?? []),
  ];
  const prereqName = prerequisiteIds.length > 0
    ? prerequisiteIds.map((id) => allNodes.get(id)?.name ?? id).join(' + ')
    : null;

  // ── LERNEND: Lernzeit läuft ────────────────────────────────────────────────
  if (cl !== null && cl.talentId === t.id) {
    const lernzeit = formatLearnCountdown(cl.completesAt);
    const pct = cl.progressPercent;
    return `
      <div class="talent-node talent-node--learning">
        <div class="talent-node-header">
          <span class="talent-node-name val-gold">${esc(t.name.toUpperCase())}</span>
          <span class="talent-node-pts">${dots}</span>
        </div>
        <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
        ${effectParts.length > 0 ? `<p class="talent-node-effect val-muted">${effectParts.map(esc).join(' &middot; ')}</p>` : ''}
        <div class="talent-learn-progress-bar">
          <div class="talent-learn-progress-fill" id="talent-learning-card-progress-${esc(t.id)}" style="width:${String(pct)}%"></div>
        </div>
        <div class="talent-learning-meta-row">
          <span class="val-muted">In:</span>
          <span class="val-gold" id="talent-learning-card-countdown-${esc(t.id)}">${lernzeit}</span>
        </div>
        <button class="talent-skip-btn" data-skip-talent="${esc(t.id)}">${String(cl.skipCostScheine)} SCHEINE - SOFORT</button>
      </div>
    `;
  }

  // ── ABGESCHLOSSEN: fertig, noch nicht abgeholt ─────────────────────────────
  if (isUncollected) {
    return `
      <div class="talent-node talent-node--uncollected">
        <div class="talent-node-header">
          <span class="talent-node-name talent-node-name--pulse">${esc(t.name.toUpperCase())}</span>
          <span class="talent-node-pts">${dots}</span>
        </div>
        <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
        ${effectParts.length > 0 ? `<p class="talent-node-effect val-green">${effectParts.map(esc).join(' &middot; ')}</p>` : ''}
        <p class="talent-ready-label">BEREIT ZUM ABHOLEN</p>
        <button class="talent-collect-btn" data-collect-talent="${esc(t.id)}">ABHOLEN</button>
      </div>
    `;
  }

  // ── AKTIV: Talent gelernt und wirksam ─────────────────────────────────────
  if (t.isActive) {
    return `
      <div class="talent-node talent-node--maxed">
        <div class="talent-node-header">
          <span class="talent-node-name val-green">${esc(t.name.toUpperCase())}</span>
          <span class="talent-node-pts">${dots}</span>
        </div>
        <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
        ${effectParts.length > 0 ? `<p class="talent-node-effect val-green">${effectParts.map(esc).join(' &middot; ')}</p>` : ''}
        <p class="talent-active-badge">[OK] AKTIV</p>
      </div>
    `;
  }

  // ── GESPERRT: Zweig gehört zum noch nicht gebauten Organisations-System (Phase 5) ──────
  if (isPhaseLocked) {
    return `
      <div class="talent-node talent-node--locked">
        <div class="talent-node-header">
          <span class="talent-node-name">${esc(t.name.toUpperCase())}</span>
          <span class="talent-node-pts">${dots}</span>
        </div>
        <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
        <p class="talent-node-req">Kommt in einer sp&auml;teren Phase (Organisations-System)</p>
      </div>
    `;
  }

  // ── GESPERRT: Level zu niedrig ────────────────────────────────────────────
  if (isLevelLocked) {
    return `
      <div class="talent-node talent-node--locked">
        <div class="talent-node-header">
          <span class="talent-node-name">${esc(t.name.toUpperCase())}</span>
          <span class="talent-node-pts">${dots}</span>
        </div>
        <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
        ${effectParts.length > 0 ? `<p class="talent-node-effect val-muted">${effectParts.map(esc).join(' &middot; ')}</p>` : ''}
        <p class="talent-node-req">Verfügbar ab Level ${String(t.minLevel)}</p>
      </div>
    `;
  }

  // ── GESPERRT: Prerequisite nicht erfüllt ──────────────────────────────────
  if (isLocked) {
    return `
      <div class="talent-node talent-node--locked">
        <div class="talent-node-header">
          <span class="talent-node-name">${esc(t.name.toUpperCase())}</span>
          <span class="talent-node-pts">${dots}</span>
        </div>
        <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
        ${prereqName !== null ? `<p class="talent-node-req">Voraussetzung: ${esc(prereqName)}</p>` : ''}
      </div>
    `;
  }

  // ── VERFUEGBAR: kann gelernt werden ──────────────────────────────────────
  const canInvest = availPts > 0 && !t.isMaxed;
  const hasActiveLearning = (learningStatus?.currentLearning ?? null) !== null;
  const lernzeitLabel = getTalentLearnTimeLabel(t.pointCost);

  // Talent-Kosten werden ALLE in NOX bezahlt (cost.blackMoney ist ebenfalls NOX)
  const balNox = player !== null ? Number(player.noxBalance) : 0;
  const totalNoxCost = t.cost.eur + t.cost.blackMoney;
  const missingNox = Math.max(0, totalNoxCost - balNox);
  const hasInsufficientFunds = missingNox > 0;

  // 70%-Regel: Spieler braucht mindestens 70% der Gesamtkosten in NOX
  const minRequiredNox = Math.floor(totalNoxCost * 0.7);
  const cannotAfford70Percent = totalNoxCost > 0 && balNox < minRequiredNox;

  let costLine = '';
  if (totalNoxCost > 0) {
    costLine = `<p class="talent-cost-line">KOSTEN: ${String(t.pointCost)} Pkt | ${formatCents(String(totalNoxCost))} ₦</p>`;
  }

  let missingLine = '';
  if (hasInsufficientFunds) {
    missingLine = `<p class="talent-missing-funds">FEHLEND: ${formatCents(String(missingNox))} ₦ → SCHULDEN</p>`;
  }

  const btnLabel = hasInsufficientFunds ? 'TROTZDEM LERNEN' : 'LERNEN';

  return `
    <div class="talent-node">
      <div class="talent-node-header">
        <span class="talent-node-name">${esc(t.name.toUpperCase())}</span>
        <span class="talent-node-pts">${dots}</span>
      </div>
      <p class="talent-node-desc"><em>${esc(t.description)}</em></p>
      ${effectParts.length > 0 ? `<p class="talent-node-effect val-green">${effectParts.map(esc).join(' &middot; ')}</p>` : ''}
      ${costLine}
      ${missingLine}
      <p class="talent-learn-time">Lernzeit: ${lernzeitLabel}</p>
      <button
        class="secondary talent-invest-btn"
        data-invest-talent="${t.id}"
        ${!canInvest || hasActiveLearning || cannotAfford70Percent ? 'disabled' : ''}
        title="${
          !canInvest
            ? t.isMaxed ? 'Bereits maximiert' : 'Keine Talentpunkte verfügbar'
            : hasActiveLearning
              ? 'Bereits in Lernzeit'
              : cannotAfford70Percent
                ? 'Mindestens 70% des Preises benötigt'
                : `${String(t.pointCost)} Talentpunkt(e) investieren und Lernzeit starten`
        }"
      >${btnLabel}</button>
    </div>
  `;
}

function renderPlaceholderTab(label: string): string {
  return `
    <section class="dash-content">
      <div class="dash-content-header">
        <h2>${esc(label.toUpperCase())}</h2>
      </div>
      <p class="dash-empty dash-empty--lg">Wird in einer späteren Phase freigeschaltet.</p>
    </section>
  `;
}

// ─── Dashboard-interner Footer ────────────────────────────────────────────────

const DASH_FOOTER_HTML = `
  <footer class="dash-main-footer">
    <nav class="dash-main-footer-links">
      <a href="/impressum">IMPRESSUM</a>
      <span>■</span>
      <a href="/datenschutz">DATENSCHUTZ</a>
      <span>■</span>
      <a href="/agb">AGB</a>
    </nav>
    <p class="dash-main-footer-disclaimer">nighttrade ist ein fiktives Spiel. Alle dargestellten Aktivitäten sind satirisch.</p>
  </footer>
`;

// ─── Haupt-Render ─────────────────────────────────────────────────────────────

function renderAll(): void {
  if (!isMounted()) return;

  const app = document.getElementById('app');
  if (!app) return;

  const player = gameState.getPlayer();
  if (!player) { navigate('/login'); return; }

  // Scroll-Position sichern — aber NICHT bei Tab-Wechseln (dort Scroll auf 0 zurücksetzen)
  const isTabSwitch = selectedTab !== lastRenderedTab;
  const savedScrollTop = isTabSwitch ? 0 : (document.querySelector<HTMLElement>('.dash-main')?.scrollTop ?? 0);
  const savedWindowScrollY = isTabSwitch ? 0 : window.scrollY;

  let mainContent: string;
  switch (selectedTab) {
    case 'jobs':      mainContent = renderJobsTab();                    break;
    case 'talents':   mainContent = renderTalentsTab(); break;
    case 'profile':   mainContent = renderProfileTab(); break;
    case 'items':                mainContent = renderPlaceholderTab('Items'); break;
    case 'warehouse':            mainContent = renderLagerhausTab();          break;
    case 'business_markt':       mainContent = renderMarktTab();              break;
    case 'business_boerse':      mainContent = renderBoerseTab();             break;
    case 'crime':                mainContent = renderCrimeOpsTab();                break;
    case 'immobilien':           mainContent = renderImmobilienTab();              break;
    case 'immobilien_lager':     mainContent = renderLagerTab();     break;
    case 'immobilien_verstecke': mainContent = renderVersteckeTab(); break;
    case 'stream':               mainContent = renderStreamTab();                   break;
    case 'market':               mainContent = renderPlaceholderTab('Marktplatz'); break;
    case 'community':            mainContent = renderCommunityTab();               break;
    case 'org':                  mainContent = renderOrgTab();                     break;
    case 'kladde':               mainContent = renderKladdeTab();                  break;
    case 'brett':                mainContent = renderBrettTab();                   break;
    default:                     mainContent = '';
  }

  app.innerHTML = `
    <div class="dashboard" id="dashboard-root">
      ${renderHeader(player)}
      <div class="dash-body">
        ${renderNav()}
        <main class="dash-main">${mainContent}${DASH_FOOTER_HTML}</main>
        ${renderSidebar(player)}
      </div>
    </div>
  `;

  // Nach Render: Tab merken für Scroll-Tracking beim nächsten Aufruf
  lastRenderedTab = selectedTab;

  // Scroll-Position wiederherstellen — bei Tab-Wechsel ist savedScrollTop = 0 → kein Restore nötig
  if (savedScrollTop > 0) {
    const dashMain = document.querySelector<HTMLElement>('.dash-main');
    if (dashMain !== null) dashMain.scrollTop = savedScrollTop;
  }
  if (savedWindowScrollY > 0) {
    window.scrollTo({ top: savedWindowScrollY, behavior: 'auto' });
  }

  attachListeners();

  // renderAll() ersetzt .dash-main bei jedem Aufruf komplett (app.innerHTML oben) — der
  // Scroll-To-Top-Button (ScrollToTop.ts) hängt seinen scroll-Listener an ein konkretes
  // Element, das dadurch bei jedem Tab-Wechsel/Poll-Refresh detached wird. Ohne diesen
  // Aufruf funktioniert der Button nur direkt nach dem allerersten Dashboard-Mount
  // (renderDashboard() ruft es dort separat auf) und danach nie wieder — Bugfix.
  updateScrollToTopContainer();

  // Community-Tab async initialisieren (Daten laden + Listener)
  if (selectedTab === 'community') {
    mountCommunityTab();
  }

  if (selectedTab === 'jobs' && jobsView === 'pfandBetrieb') void mountPfandBetriebView();
  else if (selectedTab === 'jobs' && jobsView === 'werkstatt') void mountWerkstattView();
  else if (selectedTab === 'jobs' && jobsView === 'werkstattCraft') void mountWerkstattCraftView();
  else if (selectedTab === 'stream')               mountStreamTab();
  else if (selectedTab === 'business_markt')       mountMarktTab();
  else if (selectedTab === 'business_boerse')      mountBoerseTab();
  else if (selectedTab === 'warehouse')            mountLagerhausTab();
  else if (selectedTab === 'immobilien')           mountImmobilienTab();
  else if (selectedTab === 'immobilien_lager')     mountLagerTab();
  else if (selectedTab === 'immobilien_verstecke') mountVersteckeTab();
  else if (selectedTab === 'profile')              void mountProfileTab();
  else if (selectedTab === 'kladde')               void mountKladdeTab();
  else if (selectedTab === 'brett')                void mountBrettTab();

  // Crime Ops Terminal async initialisieren (Daten laden, Timer starten)
  if (selectedTab === 'crime') {
    mountCrimeOpsTab();
  }

  if (selectedTab === 'org') {
    mountOrgTab();
  }

  // Bottom-Nav aktiven Tab synchronisieren (URL bleibt /dashboard, daher manuelle Sync)
  const bottomNavTab = (selectedTab === 'jobs' || selectedTab === 'talents' || selectedTab === 'items' || selectedTab === 'community' || selectedTab === 'crime' || selectedTab === 'immobilien' || selectedTab === 'profile')
    ? selectedTab
    : 'jobs';
  setBottomNavActiveTab(bottomNavTab);

  window.requestAnimationFrame(() => {
    syncHeaderHeightVar();
    const dashMain = document.querySelector<HTMLElement>('.dash-main');
    if (dashMain !== null) dashMain.scrollTop = savedScrollTop;
    window.scrollTo({ top: savedWindowScrollY, left: 0, behavior: 'auto' });
  });
}

// ─── Event-Handler ────────────────────────────────────────────────────────────

function attachListeners(): void {
  const root = document.getElementById('dashboard-root');
  if (!root) return;
  // Einziger Listener am Container – automatisch entfernt beim nächsten renderAll()
  root.addEventListener('click', handleClick);
}

function handleClick(e: Event): void {
  const target = e.target as Element;

  // Tab-Navigation
  const tabBtn = target.closest<HTMLButtonElement>('[data-tab]');
  if (tabBtn !== null && !tabBtn.disabled) {
    const tab = tabBtn.dataset.tab as Tab | undefined;
    if (tab !== undefined && tab !== selectedTab) {
      leaveCurrentTab();
      selectedTab = tab;
      enterTab(tab, TAB_TO_PATH[tab] ?? window.location.pathname);
      renderAll();
      return;
    }
  }

  // DIE KLADDE (NT-TUTORIAL-KLADDE-01) — Panel auf-/zuklappen (nur mobile CSS-relevant)
  const kladdeToggleBtn = target.closest<HTMLButtonElement>('[data-kladde-toggle]');
  if (kladdeToggleBtn !== null) {
    document.getElementById('kladde-panel')?.classList.toggle('is-open');
    return;
  }

  // DIE KLADDE — Home-Panel wegklicken
  const kladdeDismissBtn = target.closest<HTMLButtonElement>('[data-kladde-dismiss]');
  if (kladdeDismissBtn !== null) {
    void dismissKladdePanel().then(() => {
      if (kladdeState) kladdeState = { ...kladdeState, dismissed: true };
      renderAll();
    }).catch(() => { /* stumm ignorieren, Panel bleibt sichtbar */ });
    return;
  }

  // DIE KLADDE — JIT-Hinweis wegklicken
  const kladdeHintDismissBtn = target.closest<HTMLButtonElement>('[data-kladde-hint-dismiss]');
  if (kladdeHintDismissBtn !== null) {
    const hintKey = kladdeHintDismissBtn.dataset.kladdeHintDismiss;
    if (hintKey !== undefined) {
      void dismissKladdeHint(hintKey).then(() => {
        if (kladdeState) {
          kladdeState = {
            ...kladdeState,
            hints: kladdeState.hints.map((h) => (h.key === hintKey ? { ...h, dismissed: true } : h)),
          };
        }
        renderAll();
      }).catch(() => { /* stumm ignorieren */ });
    }
    return;
  }

  // Phase-Wechsel
  const phaseBtn = target.closest<HTMLButtonElement>('[data-phase]');
  if (phaseBtn !== null) {
    const lockedMsg = phaseBtn.dataset.lockedMsg;
    if (lockedMsg !== undefined) { showRequiredBadge(lockedMsg); return; }
    const phase = parseInt(phaseBtn.dataset.phase ?? '1', 10);
    if (phase !== selectedPhase) { selectedPhase = phase; renderAll(); return; }
  }

  // Job starten
  const startBtn = target.closest<HTMLButtonElement>('[data-start-job]');
  if (startBtn !== null && !startBtn.disabled) {
    const jobId = startBtn.dataset.startJob;
    if (jobId !== undefined) { void handleStartJob(jobId); return; }
  }

  // Job beenden (Schicht abschließen)
  const completeBtn = target.closest<HTMLButtonElement>('[data-complete-job]');
  if (completeBtn !== null && !completeBtn.disabled) {
    const jobId = completeBtn.dataset.completeJob;
    if (jobId !== undefined) { void handleCompleteJob(jobId); return; }
  }

  // Cooldown überspringen
  const skipBtn = target.closest<HTMLButtonElement>('[data-skip-cooldown]');
  if (skipBtn !== null && !skipBtn.disabled) {
    const jobId = skipBtn.dataset.skipCooldown;
    if (jobId !== undefined) { void handleSkipCooldown(jobId); return; }
  }

  // Pfand-Modal öffnen (NT-PFAND-SAMMLER-01)
  if (target.closest('[data-pfand-open]') !== null) {
    void openPfandModal();
    return;
  }

  // Jobs-Unteransichten (Pfand-Betrieb, Werkstatt, Craft) — NT-LAYOUT-01: Wechsel nur noch
  // über die URL (navigate → renderDashboard → switchJobsView), damit Browser-Zurück funktioniert.
  // Pfand-Betrieb-Panel öffnen (NT-PFAND-IMPERIUM-01, seit NT-PFAND-UI-JOBFENSTER-01 View statt Modal)
  if (target.closest('[data-pfand-betrieb-open]') !== null) {
    navigate(JOBS_VIEW_PATHS.pfandBetrieb);
    return;
  }

  // Zurück von der Pfand-Betrieb-View zur Jobs-Liste
  if (target.closest('[data-pfand-betrieb-back]') !== null) {
    navigate(JOBS_VIEW_PATHS.list);
    return;
  }

  // Werkstatt-Verwaltungsseite öffnen (NT-WERKSTATT-BETRIEB-02B)
  if (target.closest('[data-werkstatt-open]') !== null) {
    navigate(JOBS_VIEW_PATHS.werkstatt);
    return;
  }

  // Zurück von der Werkstatt-View zur Jobs-Liste
  if (target.closest('[data-werkstatt-back]') !== null) {
    navigate(JOBS_VIEW_PATHS.list);
    return;
  }

  // Craft-Ansicht öffnen (NT-WERKSTATT-CRAFT-02) — nur aus der Werkstatt-View heraus erreichbar
  if (target.closest('[data-werkstatt-craft-open]') !== null) {
    navigate(JOBS_VIEW_PATHS.werkstattCraft);
    return;
  }

  // Zurück von der Craft-Ansicht zur Werkstatt-View (nicht zur Jobs-Liste)
  if (target.closest('[data-werkstatt-craft-back]') !== null) {
    navigate(JOBS_VIEW_PATHS.werkstatt);
    return;
  }

  // Job zur Queue hinzufügen
  const queueAddBtn = target.closest<HTMLButtonElement>('[data-queue-add]');
  if (queueAddBtn !== null && !queueAddBtn.disabled) {
    const jobId = queueAddBtn.dataset.queueAdd;
    if (jobId !== undefined) { void handleQueueAdd(jobId); return; }
  }

  // Job aus Queue entfernen
  const queueRemoveBtn = target.closest<HTMLButtonElement>('[data-queue-remove]');
  if (queueRemoveBtn !== null && !queueRemoveBtn.disabled) {
    const entryId = queueRemoveBtn.dataset.queueRemove;
    if (entryId !== undefined) { void handleQueueRemove(entryId); return; }
  }

  // Hamburger-Menü
  if (target.closest('#dash-hamburger') !== null) { toggleHamburgerMenu(); return; }

  // Benachrichtigungs-Glocke
  if (target.closest('#notif-bell-btn') !== null) { toggleNotificationBell(); return; }

  // Abmelden
  if (target.closest('#dash-logout-btn') !== null) { void handleLogout(); return; }

  // Fahndungs-Detail-Popup toggle (Sidebar + Header)
  const wantedToggleBtn = target.closest<HTMLElement>('[data-wanted-toggle]');
  if (wantedToggleBtn !== null) {
    const detailId = wantedToggleBtn.dataset.wantedToggle;
    const arrowId = wantedToggleBtn.dataset.wantedArrow;
    if (detailId === undefined) return;
    const detail = document.getElementById(detailId);
    const arrow = arrowId !== undefined ? document.getElementById(arrowId) : null;
    if (detail) {
      const isOpen = detail.style.display !== 'none';
      detail.style.display = isOpen ? 'none' : 'block';
      if (arrow) arrow.textContent = isOpen ? '▼' : '▲';
    }
    return;
  }

  // Talent-Accordion Zweig aufklappen / zuklappen
  const toggleBtn = target.closest<HTMLButtonElement>('[data-toggle-branch]');
  if (toggleBtn !== null) {
    const branch = toggleBtn.dataset.toggleBranch;
    if (branch === undefined) return;
    const item = toggleBtn.closest<HTMLElement>('[data-accordion-branch]');
    if (item === null) return;
    const isNowOpen = openBranches.has(branch);
    const toggleLabel = toggleBtn.querySelector('.talent-accordion-toggle');
    if (isNowOpen) {
      openBranches.delete(branch);
      item.classList.remove('talent-accordion-item--open');
      if (toggleLabel) toggleLabel.textContent = '[+]';
    } else {
      openBranches.add(branch);
      item.classList.add('talent-accordion-item--open');
      if (toggleLabel) toggleLabel.textContent = '[-]';
    }
    return;
  }

  // Talent investieren (Lernzeit starten)
  const investBtn = target.closest<HTMLButtonElement>('[data-invest-talent]');
  if (investBtn !== null && !investBtn.disabled) {
    const talentId = investBtn.dataset.investTalent;
    if (talentId !== undefined) { void handleInvestPoint(talentId); return; }
  }

  // Talent abholen (nach Lernzeit)
  const collectBtn = target.closest<HTMLButtonElement>('[data-collect-talent]');
  if (collectBtn !== null && !collectBtn.disabled) {
    const talentId = collectBtn.dataset.collectTalent;
    if (talentId !== undefined) { void handleCollectTalent(talentId); return; }
  }

  // Talent-Lernzeit überspringen
  const skipTalentBtn = target.closest<HTMLButtonElement>('[data-skip-talent]');
  if (skipTalentBtn !== null && !skipTalentBtn.disabled) {
    const talentId = skipTalentBtn.dataset.skipTalent;
    if (talentId !== undefined) { void handleSkipTalentLearning(talentId); return; }
  }

  // Schulden tilgen
  const repayDebtBtn = target.closest<HTMLButtonElement>('[data-repay-debt]');
  if (repayDebtBtn !== null && !repayDebtBtn.disabled) { void handleRepayDebt(); return; }

  // Modal schließen (Button mit data-close-modal oder Klick auf Backdrop)
  // Event-Popups (event-popup-backdrop) dürfen NICHT durch Außenklick geschlossen werden –
  // Spieler muss wählen oder Timer ablaufen lassen.
  // .modal--no-dismiss (NT-UI-MODAL-LISTE-01): Modal erzwingt Bestätigung über den eigenen
  // Button – Backdrop-Klick wird für dieses eine Modal unterdrückt, alle anderen Modals bleiben
  // unberührt.
  const closeBtn = target.closest<HTMLElement>('[data-close-modal]');
  if (closeBtn !== null) {
    const backdrop = closeBtn.closest('.modal-backdrop');
    if (backdrop !== null && !backdrop.querySelector('.modal--no-dismiss')) { backdrop.remove(); }
    return;
  }
  if ((target as HTMLElement).classList.contains('modal-backdrop') &&
      !(target as HTMLElement).classList.contains('event-popup-backdrop') &&
      !(target as HTMLElement).querySelector('.modal--no-dismiss')) {
    (target as HTMLElement).remove();
    return;
  }
}

// ─── Aktionen ─────────────────────────────────────────────────────────────────

async function handleStartJob(jobId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-start-job="${CSS.escape(jobId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  try {
    const result = await api.post<{ activeJob: ActiveJobTickState }>('/jobs/start', { jobId });
    currentActiveJob = result.activeJob;
    startProgressTimer();
    scheduleNextTick(result.activeJob.nextTickInSeconds);
    // Status aktualisieren und neu rendern
    const status = await api.get<JobStatusResponse>('/jobs/status');
    applyJobStatus(status);
    renderAll();
    syncPfandSchichtStatus();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
    if (btn !== null && isMounted()) {
      btn.disabled = false;
      btn.textContent = '\u25b6 STARTEN';
    }
  }
}

async function handleCompleteJob(jobId?: string): Promise<void> {
  const jId = jobId ?? currentActiveJob?.jobId;
  if (jId === undefined) return;

  // Guard: verhindert doppelten Abschluss wenn updateProgressInPlace und Klick kollidieren
  if (isCompletingJob) return;
  isCompletingJob = true;

  const btn = document.querySelector<HTMLButtonElement>(`[data-complete-job="${CSS.escape(jId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  // Laufenden Tick-Timer stoppen
  if (tickTimer !== null) { clearTimeout(tickTimer); tickTimer = null; }

  try {
    const result = await api.post<CompleteJobResult>('/jobs/complete', {});
    currentActiveJob = null;
    stopProgressTimer();

    // Profil, Status und Talentbaum neu laden (Level-Up kann Talentpunkte vergeben)
    const [profile, status, tree] = await Promise.all([
      api.get<PlayerSnapshot>('/auth/me'),
      api.get<JobStatusResponse>('/jobs/status'),
      api.get<TalentTreeData>('/talents/tree'),
    ]);
    gameState.setPlayer(profile);
    applyJobStatus(status);
    talentTree = tree;
    talentTreeLoadFailed = false;
    // Cooldown-Timer starten falls Cooldown vorhanden
    startProgressTimer();
    renderAll();

    isCompletingJob = false;
    showCompleteResult(result);
    if (result.summary.queueStarted !== null) {
      const startedJobName = allJobs.find((j) => j.id === result.summary.queueStarted)?.name ?? result.summary.queueStarted;
      showToast(`[JOB GESTARTET] ${startedJobName} läuft jetzt.`, 'info');
    } else if (result.summary.queueError !== null) {
      showToast(`[QUEUE] ${result.summary.queueError}`, 'error');
    }

    // Polizei-Event nach Crime-Job-Abschluss anzeigen
    if (result.summary.policeEventData) {
      showPoliceEventModal(result.summary.policeEventData);
    }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.code === 'TOO_EARLY_TO_STOP') {
        showToast(err.message, 'info');
      } else {
        showToast(err.message, 'error');
      }
    }
    isCompletingJob = false;
    if (btn !== null && isMounted()) { btn.disabled = false; }  // Text wird durch updateProgressInPlace gesetzt
  }
}

async function handleTick(): Promise<void> {
  if (!isMounted()) { cleanup(); return; }
  if (currentActiveJob === null) return;

  try {
    const result = await api.post<TickResult>('/jobs/tick', {});

    // Nächsten Tick schedulen (wenn Schicht noch läuft)
    scheduleNextTick(result.nextTickInSeconds);

    // Event-Popup anzeigen
    if (result.event !== null) {
      recentEvents.unshift(result.event.name);
      if (recentEvents.length > 10) recentEvents.pop();
      showEventPopup(result.event);
      // NT-PFAND-SCHICHT-01: Event kann Flaschen in den Wagen gelegt haben
      syncPfandSchichtStatus();
      // Balance + Ruf live aktualisieren: frisches Profil laden
      void api.get<PlayerSnapshot>('/auth/me').then((fresh) => {
        if (!isMounted()) return;
        gameState.setPlayer(fresh);
        const balanceEl = document.getElementById('sidebar-balance');
        if (balanceEl !== null) balanceEl.textContent = `${formatCents(fresh.noxBalance)} ₦`;
        const blackEl = document.getElementById('sidebar-black-money');
        if (blackEl !== null) blackEl.textContent = `${formatCents(fresh.blackMoney)} S-₦`;
        const hNox = document.getElementById('header-nox-balance');
        if (hNox !== null) hNox.textContent = `${formatCents(fresh.noxBalance)} ₦`;
        const hBm = document.getElementById('header-black-money');
        if (hBm !== null) hBm.textContent = `${formatCents(fresh.blackMoney)} S-₦`;
        const repEl = document.getElementById('sidebar-reputation');
        if (repEl !== null) repEl.textContent = `${String(fresh.reputation ?? 0)} / 1000`;
        const piEl = document.getElementById('sidebar-pi');
        if (piEl !== null && fresh.politicalInfluence !== undefined) {
          piEl.textContent = `${String(fresh.politicalInfluence)} / 1000`;
        }
      }).catch(() => { /* Nicht-kritisch: nächster Sync folgt */ });
    }

    // FL3-Patrol: Polizei-Event während laufender Schicht
    if (result.patrolEvent) {
      showPoliceEventModal(result.patrolEvent);
    }

    // Event-Log in Sidebar aktualisieren
    const logEl = document.getElementById('dash-event-log');
    if (logEl !== null) {
      logEl.innerHTML = recentEvents.length === 0
        ? '<p class="dash-empty">Keine Events.</p>'
        : recentEvents.map((ev) => `<p class="event-log-entry">${esc(ev)}</p>`).join('');
    }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 429) {
        scheduleNextTick(30);
      } else if (err.code === 'SHIFT_ENDED') {
        // Nur abschließen wenn Seite sichtbar – sonst erledigt handlePageVisible() es
        if (document.visibilityState === 'visible') {
          void handleCompleteJob();
        }
      } else {
        showToast(err.message, 'error');
        scheduleNextTick(30);
      }
    }
  }
}

function scheduleNextTick(nextTickInSeconds: number): void {
  if (tickTimer !== null) clearTimeout(tickTimer);
  tickTimer = setTimeout(() => {
    if (!isMounted()) { cleanup(); return; }
    void handleTick();
  }, nextTickInSeconds * 1_000);
}

function buildOutcomePanelData(result: EventResolutionResult): EventOutcomePanelData {
  const eur = result.appliedEffects.eurDelta ?? 0;
  const rep = result.appliedEffects.reputationDelta ?? 0;
  const wanted = result.appliedEffects.wantedLevelDelta ?? 0;
  const shiftTimePenalty = result.appliedEffects.shiftTimePenaltySeconds;

  // Item-Drops aus appliedEffects als lesbaren String zusammenführen
  const drops = result.appliedEffects.itemDropResults ?? [];
  const itemFound = drops.length > 0
    ? drops
        .filter((d) => d.itemType !== null)
        .map((d) => {
          const label = d.itemLabel ?? formatItemKey(d.itemType ?? '');
          const qty = d.quantity > 0 ? d.quantity : 1;
          if (d.destinationType === 'AUTO_SOLD') return `${String(qty)}x ${label} (verkauft)`;
          if (d.dropped) return `+${String(qty)}x ${label}`;
          return `${String(qty)}x ${label} (liegengelassen)`;
        })
        .join(', ') || null
    : null;

  const isPositive = eur > 0 || rep > 0 || (itemFound !== null && drops.some((d) => d.dropped));
  const isNegative = eur < 0 || rep < 0 || wanted > 0 || (shiftTimePenalty !== undefined && shiftTimePenalty > 0);
  let flavor: EventOutcomePanelData['flavor'] = 'neutral';
  if (isPositive && isNegative) flavor = 'mixed';
  else if (isPositive) flavor = 'positive';
  else if (isNegative) flavor = 'negative';

  return {
    choiceId: result.choiceKey,
    narrativeText: result.outcome.logMessage,
    rewards: {
      eur: String(eur),
      blackMoney: '0',
      xp: 0,
      reputationChange: rep,
    },
    consequences: {
      wantedLevelChange: wanted,
      itemFound,
      contactUnlocked: null,
      ...(shiftTimePenalty !== undefined ? { shiftTimePenalty } : {}),
    },
    flavor,
    isRetroactive: false,
  };
}

async function handleEventRespond(jobEventLogId: string, choiceKey: string): Promise<void> {
  try {
    const result = await api.post<EventResolutionResult>(
      `/jobs/events/${encodeURIComponent(jobEventLogId)}/resolve`,
      { choiceKey },
    );
    showEventOutcomePanel({
      outcome: buildOutcomePanelData(result),
      onClose: () => { /* Panel schliesst sich selbst */ },
    });
    // Schicht-Countdown sofort aktualisieren wenn Zeitstrafe angewendet wurde
    if (result.appliedEffects.shiftTimePenaltySeconds !== undefined) {
      void api.get<JobStatusResponse>('/jobs/status').then((status) => {
        if (!isMounted()) return;
        applyJobStatus(status);
      }).catch(() => { /* Nicht-kritisch: nächster Tick synchronisiert */ });
    }
    // Balance + Ruf nach Event-Auflösung live aktualisieren
    void api.get<PlayerSnapshot>('/auth/me').then((fresh) => {
      if (!isMounted()) return;
      gameState.setPlayer(fresh);
      const balanceEl = document.getElementById('sidebar-balance');
      if (balanceEl !== null) balanceEl.textContent = `${formatCents(fresh.noxBalance)} ₦`;
      const blackEl = document.getElementById('sidebar-black-money');
      if (blackEl !== null) blackEl.textContent = `${formatCents(fresh.blackMoney)} S-₦`;
      const hNox = document.getElementById('header-nox-balance');
      if (hNox !== null) hNox.textContent = `${formatCents(fresh.noxBalance)} ₦`;
      const hBm = document.getElementById('header-black-money');
      if (hBm !== null) hBm.textContent = `${formatCents(fresh.blackMoney)} S-₦`;
      const repEl = document.getElementById('sidebar-reputation');
      if (repEl !== null) repEl.textContent = `${String(fresh.reputation ?? 0)} / 1000`;
      const piEl = document.getElementById('sidebar-pi');
      if (piEl !== null && fresh.politicalInfluence !== undefined) {
        piEl.textContent = `${String(fresh.politicalInfluence)} / 1000`;
      }
    }).catch(() => { /* Nicht-kritisch: nächster Sync folgt */ });
  } catch (err) {
    if (err instanceof ApiError) {
      showToast(err.message, 'error');
    } else {
      showToast('Ereignis konnte nicht verarbeitet werden.', 'error');
    }
  }
}

async function handleSkipCooldown(jobId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-skip-cooldown="${CSS.escape(jobId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post('/jobs/cooldown/skip', { jobId });
    const status = await api.get<JobStatusResponse>('/jobs/status');
    applyJobStatus(status);
    renderAll();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
    if (btn !== null && isMounted()) { btn.disabled = false; btn.textContent = '5 SCHEINE SKIP'; }
  }
}

async function handleQueueAdd(jobId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-queue-add="${CSS.escape(jobId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post('/jobs/queue/add', { jobId });
    const status = await api.get<JobStatusResponse>('/jobs/status');
    applyJobStatus(status);
    renderAll();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
    if (btn !== null && isMounted()) { btn.disabled = false; btn.textContent = 'IN QUEUE +'; }
  }
}

async function handleQueueRemove(entryId: string): Promise<void> {
  try {
    await api.delete(`/jobs/queue/${encodeURIComponent(entryId)}`);
    const status = await api.get<JobStatusResponse>('/jobs/status');
    applyJobStatus(status);
    renderAll();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
  }
}

async function handleLogout(): Promise<void> {
  cleanup();
  await logoutUser();
  navigate('/login');
}

async function handleInvestPoint(talentId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-invest-talent="${CSS.escape(talentId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  try {
    // POST gibt jetzt InvestPointsResult zurück (Lernzeit-Informationen)
    const result = await api.post<InvestPointsResult>('/talents/invest', { talentId, points: 1 });
    // Baum, Lernstatus und Spielerprofil atomar aktualisieren
    const [tree, learning, profile, debt] = await Promise.all([
      api.get<TalentTreeData>('/talents/tree'),
      api.get<LearningStatusResponse>('/talents/learning'),
      api.get<PlayerSnapshot>('/auth/me'),
      api.get<DebtStatus>('/talents/debt'),
    ]);
    talentTree = tree;
    learningStatus = learning;
    gameState.setPlayer(profile);
    debtStatus = debt;
    if (learning.currentLearning !== null) startProgressTimer();
    if (result.debtCreated !== null && (result.debtCreated.eur !== '0' || result.debtCreated.blackMoney !== '0')) {
      const debtParts: string[] = [];
      if (result.debtCreated.eur !== '0') debtParts.push(`${formatCents(result.debtCreated.eur)} ₦`);
      if (result.debtCreated.blackMoney !== '0') debtParts.push(`${formatCents(result.debtCreated.blackMoney)} S-₦`);
      showToast(`${result.learning.talentName}: Lernzeit gestartet. Schulden angelegt: ${debtParts.join(' | ')}`, 'info');
    } else {
      const tierLabels = { TIER_1: '30 Min', TIER_2: '2 Stunden', TIER_3: '8 Stunden' } as const;
      showToast(`${result.learning.talentName}: Lernzeit ${tierLabels[result.learning.tier]} gestartet.`, 'info');
    }
    renderAll();
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.code === 'ALREADY_LEARNING') {
        showToast('Du lernst bereits ein Talent. Warte bis es abgeschlossen ist.', 'info');
      } else {
        showToast(err.message, 'error');
      }
    }
    if (btn !== null && isMounted()) { btn.disabled = false; btn.textContent = 'LERNEN'; }
  }
}

async function handleCollectTalent(talentId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-collect-talent="${CSS.escape(talentId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post('/talents/learning/collect', { talentId });
    const [tree, learning, profile] = await Promise.all([
      api.get<TalentTreeData>('/talents/tree'),
      api.get<LearningStatusResponse>('/talents/learning'),
      api.get<PlayerSnapshot>('/auth/me'),
    ]);
    talentTree = tree;
    learningStatus = learning;
    gameState.setPlayer(profile);
    showToast('Talent gelernt!', 'info');
    renderAll();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
    if (btn !== null && isMounted()) { btn.disabled = false; btn.textContent = 'ABHOLEN'; }
  }
}

/** Zeigt ein Modal wenn der Spieler nicht genug Scheine hat */
function showInsufficientScheineModal(currentScheine: number, requiredScheine: number): void {
  const root = document.getElementById('dashboard-root');
  if (root === null) return;

  const modal = document.createElement('div');
  modal.className = 'modal-backdrop';
  modal.setAttribute('role', 'alertdialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Nicht genug Scheine');
  modal.innerHTML = `
    <div class="modal modal--narrow">
      <div class="modal-header">
        <h2>NICHT GENUG SCHEINE</h2>
      </div>
      <div class="modal-body">
        <p>Du brauchst <strong>${String(requiredScheine)} Scheine</strong>, um die Lernzeit zu überspringen.</p>
        <p class="modal-stat-label">DEIN KONTOSTAND: <span class="val-gold">${String(currentScheine)} Scheine</span></p>
        <p class="text--muted">Die Lernzeit läuft normal weiter.</p>
      </div>
      <div class="modal-actions">
        <button class="btn btn--primary" data-close-modal>OK</button>
      </div>
    </div>
  `;
  root.appendChild(modal);
}

async function handleSkipTalentLearning(talentId: string): Promise<void> {
  // Kosten kommen vom Server (tierabhängig) — hier nur aus dem bereits geladenen Lernstatus lesen
  const cl = learningStatus?.currentLearning;
  const skipCost = cl?.talentId === talentId ? cl.skipCostScheine : 0;

  // Frontend-Vorprüfung: Scheine aus State lesen bevor der Request gestartet wird
  const currentScheine = gameState.getPlayer()?.scheine ?? 0;
  if (currentScheine < skipCost) {
    showInsufficientScheineModal(currentScheine, skipCost);
    return;
  }

  const btn = document.querySelector<HTMLButtonElement>(`[data-skip-talent="${CSS.escape(talentId)}"]`);
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }

  try {
    const skip = await api.post<SkipLearningResult>('/talents/learning/skip', { talentId });

    // Scheine-Balance sofort im Header aktualisieren (kein Warten auf /auth/me)
    const headerScheine = document.getElementById('header-scheine');
    if (headerScheine !== null) headerScheine.textContent = String(skip.scheineRemaining);

    // Inline-Meldung unter dem Button (Dauer nach Textlänge)
    const msgEl = document.createElement('div');
    msgEl.className = 'talent-skip-msg';
    msgEl.textContent = `Talent abgeschlossen. -${String(skip.scheineSpent)} Scheine.`;
    btn?.parentElement?.appendChild(msgEl);
    setTimeout(() => { msgEl.remove(); }, calcNoticeDisplayMs(msgEl.textContent));

    const [tree, learning, profile] = await Promise.all([
      api.get<TalentTreeData>('/talents/tree'),
      api.get<LearningStatusResponse>('/talents/learning'),
      api.get<PlayerSnapshot>('/auth/me'),
    ]);
    talentTree = tree;
    learningStatus = learning;
    gameState.setPlayer(profile);
    renderAll();
  } catch (err) {
    if (btn !== null && isMounted()) { btn.disabled = false; btn.textContent = `${String(skipCost)} SCHEINE - SOFORT`; }
    // Backend-Ablehnung: Modal statt Toast — Lernzeit läuft weiter
    if (err instanceof ApiError && err.code === 'INSUFFICIENT_SCHEINE') {
      showInsufficientScheineModal(gameState.getPlayer()?.scheine ?? 0, skipCost);
      return;
    }
    if (err instanceof ApiError) showToast(err.message, 'error');
  }
}

async function handleRepayDebt(): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>('[data-repay-debt]');
  if (btn !== null) { btn.disabled = true; btn.textContent = '...'; }
  if (debtStatus === null) return;

  try {
    const result = await api.post<RepayDebtResult>('/talents/debt/repay', {
      amountEur: debtStatus.debtNox,
      amountBlackMoney: debtStatus.debtBlackMoney,
    });
    const [fresh, profile] = await Promise.all([
      api.get<DebtStatus>('/talents/debt'),
      api.get<PlayerSnapshot>('/auth/me'),
    ]);
    debtStatus = fresh;
    gameState.setPlayer(profile);
    const msg = result.debtCleared
      ? 'Schulden vollständig getilgt.'
      : `Teilgetilgt. Verbleibend: ${formatCents(result.remainingDebtEur)} ₦`;
    showToast(msg, 'info');
    renderAll();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
    if (btn !== null && isMounted()) { btn.disabled = false; btn.textContent = 'SCHULDEN TILGEN'; }
  }
}

// ─── Polling & Progress-Timer ─────────────────────────────────────────────────

/** Job-Status (Cooldowns, Queue, aktiver Job) vom Server abrufen und State aktualisieren */
async function syncJobStatus(): Promise<void> {
  if (!isMounted()) { cleanup(); return; }
  try {
    const status = await api.get<JobStatusResponse>('/jobs/status');
    const prevActiveJobId = currentActiveJob?.jobId ?? null;
    applyJobStatus(status);
    const newActiveJobId = currentActiveJob?.jobId ?? null;

    // Wenn Queue-Automation einen neuen Job gestartet hat → Tick schedulen
    if (newActiveJobId !== prevActiveJobId) {
      if (currentActiveJob !== null) {
        startProgressTimer();
        scheduleNextTick(currentActiveJob.nextTickInSeconds);
      } else {
        stopProgressTimer();
      }
      renderAll();
    }
  } catch {
    // Polling-Fehler stumm ignorieren
  }
}

/** Status-Sync alle 60 Sekunden (hält Queue und Cooldowns aktuell) */
function startStatusPolling(): void {
  if (pollingTimer !== null) return;
  pollingTimer = setInterval(() => {
    void syncJobStatus();
  }, 60_000);
}

/** Talent-Lernstatus vom Server abrufen und auf geänderungen prüfen */
async function syncLearningStatus(): Promise<void> {
  if (!isMounted()) return;
  try {
    const [status, tree] = await Promise.all([
      api.get<LearningStatusResponse>('/talents/learning'),
      api.get<TalentTreeData>('/talents/tree'),
    ]);

    const prevLearning = learningStatus?.currentLearning?.talentId ?? null;
    const prevUncollected = learningStatus?.completedButUncollected.length ?? 0;
    const prevTreeRef = talentTree;
    learningStatus = status;
    talentTree = tree;
    talentTreeLoadFailed = false;

    // Neu abgeschlossene Talente per Toast melden (completedButUncollected oder Cron-Aktivierung)
    const newUncollected = status.completedButUncollected.length;
    if (newUncollected > prevUncollected) {
      const newest = status.completedButUncollected[newUncollected - 1];
      if (newest !== undefined) {
        showToast(`TALENT GELERNT: ${newest.talentName} – Jetzt abholen!`, 'info');
      }
    } else if (prevLearning !== null && status.currentLearning === null && newUncollected === 0) {
      // Cron hat Talent direkt aktiviert (kein "ABHOLEN" nötig) → Toast
      const activatedNode = TALENT_BRANCH_KEYS
        .flatMap((branch) => tree.branches[branch])
        .find((t) => t.id === prevLearning && t.isActive);
      if (activatedNode !== undefined) {
        showToast(`TALENT AKTIV: ${activatedNode.name}`, 'info');
      }
    }

    const nextLearning = status.currentLearning?.talentId ?? null;
    const treeChanged = prevTreeRef !== tree;
    const learningStateChanged = prevLearning !== nextLearning || prevUncollected !== newUncollected;

    // Nur bei echten Strukturänderungen full rerendern; Countdowns aktualisiert updateProgressInPlace.
    if (selectedTab === 'talents' && (learningStateChanged || treeChanged)) {
      renderAll();
    }
  } catch {
    // Polling-Fehler stumm ignorieren
  }
}

/** Job-Status-Response auf lokalen State anwenden */
function applyJobStatus(status: JobStatusResponse): void {
  allJobs = status.allJobs;
  // Backend liefert cooldownEndsAt als string | null – Einträge ohne Datum filtern
  cooldowns = status.cooldowns.filter(
    (cd): cd is { jobId: string; cooldownEndsAt: string } => cd.cooldownEndsAt !== null,
  );
  queue = status.queue;
  queueMaxSlots = status.queueMaxSlots;
  // Backend liefert activeJobs[] (Array), Frontend nutzt erstes Element oder null
  currentActiveJob = status.activeJobs[0] ?? null;
}

function startProgressTimer(): void {
  // Timer läuft wenn Job aktiv, Cooldowns vorhanden oder Talent in Lernzeit
  if (!currentActiveJob && cooldowns.length === 0 && (learningStatus?.currentLearning ?? null) === null) return;
  if (progressTimer !== null) return;
  progressTimer = setInterval(() => {
    if (!isMounted()) { cleanup(); return; }
    updateProgressInPlace();
  }, 1_000);
}

function stopProgressTimer(): void {
  if (progressTimer !== null) { clearInterval(progressTimer); progressTimer = null; }
}

/** Countdowns in-place aktualisieren ohne Full-Re-render */
function updateProgressInPlace(): void {
  const now = Date.now();

  // Schicht-Fortschritt und Countdown nur wenn Job aktiv und shiftEndsAt gesetzt
  if (currentActiveJob?.shiftEndsAt) {
    const shiftEndTime = new Date(currentActiveJob.shiftEndsAt).getTime();

    // Schicht abgelaufen? Auto-Complete nur wenn Seite sichtbar (PWA: Modal muss angezeigt werden)
    if (now >= shiftEndTime) {
      stopProgressTimer();
      if (tickTimer !== null) { clearTimeout(tickTimer); tickTimer = null; }
      // Nur abschließen wenn Tab sichtbar und kein Offline-Recap offen
      if (document.visibilityState === 'visible' && !isRecapModalOpen) {
        void handleCompleteJob();
      }
      return;
    }

    const jobId = currentActiveJob.jobId;

    // Shift-Countdown
    const shiftEl = document.getElementById(`shift-countdown-${jobId}`);
    if (shiftEl !== null) shiftEl.textContent = formatCountdown(currentActiveJob.shiftEndsAt);

    // Shift-Fortschrittsbalken
    const progressEl = document.getElementById(`shift-progress-${jobId}`);
    if (progressEl !== null) {
      const pct = calcShiftProgress(currentActiveJob.startedAt, currentActiveJob.shiftEndsAt);
      progressEl.style.setProperty('--shift-progress', `${String(Math.floor(pct))}%`);
    }

    // Stop-Button dynamisch aktualisieren (Abbruchgrenze: 20% oder 50%)
    const jobDef = allJobs.find((j) => j.id === currentActiveJob?.jobId);
    if (jobDef !== undefined) {
      const shiftDurationMs = jobDef.shiftDurationMinutes * 60 * 1000;
      const elapsed = now - new Date(currentActiveJob.startedAt).getTime();
      const shiftProgress = Math.min(1, elapsed / shiftDurationMs);
      const requiredCompletion = getRequiredShiftCompletion(jobDef.phase, currentActiveJob.jobLevel);
      const canStop = shiftProgress >= requiredCompletion;

      const btn = document.querySelector<HTMLButtonElement>(
        `[data-complete-job="${CSS.escape(currentActiveJob.jobId)}"]`,
      );
      if (btn !== null && !btn.classList.contains('btn-complete--ready')) {
        if (canStop) {
          btn.disabled = false;
          btn.classList.remove('btn-complete--locked');
          // Anzeigeprozentwert basiert auf tats\u00e4chlicher Schichtdauer (inkl. Zeitstrafen durch Events)
          const actualDurationMs = shiftEndTime - new Date(currentActiveJob.startedAt).getTime();
          const pct = Math.floor(Math.min(1, elapsed / actualDurationMs) * 100);
          btn.textContent = `\u25a0 BEENDEN (${String(pct)}%)`;
        } else {
          btn.disabled = true;
          btn.classList.add('btn-complete--locked');
          const halfwayMs = shiftDurationMs * requiredCompletion - elapsed;
          const countdown = formatCountdown(new Date(now + Math.max(0, halfwayMs)).toISOString());
          btn.textContent = countdown;
        }
      }
    }
  }

  // Cooldown-Countdowns (laufen unabhängig vom aktiven Job)
  cooldowns.forEach((cd) => {
    const el = document.getElementById(`cooldown-countdown-${cd.jobId}`);
    if (el === null) return;
    const ms = new Date(cd.cooldownEndsAt).getTime() - now;
    if (ms <= 0) {
      // Cooldown abgelaufen → Status neu laden + rendern (syncJobStatus ruft renderAll intern auf)
      void syncJobStatus().then(() => { renderAll(); }).catch(() => { renderAll(); });
    } else {
      el.textContent = formatCountdown(cd.cooldownEndsAt);
    }
  });

  // Talent-Lernzeit-Countdown in-place aktualisieren (nur wenn Talent-Tab aktiv)
  if (selectedTab === 'talents') {
    const cl = learningStatus?.currentLearning ?? null;
    if (cl !== null) {
      // Panel-Countdown und -Fortschritt
      const panelCountdown = document.getElementById('talent-learning-countdown');
      if (panelCountdown !== null) panelCountdown.textContent = formatLearnCountdown(cl.completesAt);

      const totalMs = new Date(cl.completesAt).getTime() - new Date(cl.startedAt).getTime();
      const elapsedMs = Date.now() - new Date(cl.startedAt).getTime();
      const pct = totalMs <= 0 ? 100 : Math.min(100, Math.floor((elapsedMs / totalMs) * 100));

      const panelProgress = document.getElementById('talent-learning-progress');
      if (panelProgress !== null) panelProgress.style.width = `${String(pct)}%`;

      // Card-Countdown und -Fortschritt
      const cardCountdown = document.getElementById(`talent-learning-card-countdown-${cl.talentId}`);
      if (cardCountdown !== null) cardCountdown.textContent = formatLearnCountdown(cl.completesAt);

      const cardProgress = document.getElementById(`talent-learning-card-progress-${cl.talentId}`);
      if (cardProgress !== null) cardProgress.style.width = `${String(pct)}%`;

      // Lernzeit abgelaufen? Learning-Status neu laden
      if (new Date(cl.completesAt).getTime() <= Date.now()) {
        void syncLearningStatus();
      }
    }
  }

  // Timer stoppen wenn weder aktiver Job noch Cooldowns noch Lernzeit vorhanden
  if (!currentActiveJob && cooldowns.length === 0 && (learningStatus?.currentLearning ?? null) === null) {
    stopProgressTimer();
  }
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadDashboardData(): Promise<void> {
  try {
    const [status, profile] = await Promise.all([
      api.get<JobStatusResponse>('/jobs/status'),
      api.get<PlayerSnapshot>('/auth/me'),
    ]);
    applyJobStatus(status);
    gameState.setPlayer(profile);

    // Tick-Timer starten wenn Job bereits aktiv
    if (currentActiveJob) {
      startProgressTimer();
      scheduleNextTick(currentActiveJob.nextTickInSeconds);
      syncPfandSchichtStatus();
    } else if (cooldowns.length > 0) {
      // Kein aktiver Job, aber Cooldown läuft → Countdown-Timer starten
      startProgressTimer();
    }

    // Talentbaum + Lernstatus separat laden – Fehler blockieren nicht die restliche UI
    api.get<TalentTreeData>('/talents/tree').then((data) => {
      talentTree = data;
      talentTreeLoadFailed = false;
      if (selectedTab === 'talents') renderAll();
    }).catch((err: unknown) => {
      console.error('[Dashboard] Talentbaum konnte nicht geladen werden:', err);
      talentTreeLoadFailed = true;
      if (selectedTab === 'talents') renderAll();
    });

    api.get<LearningStatusResponse>('/talents/learning').then((data) => {
      learningStatus = data;
      if (data.currentLearning !== null) startProgressTimer();
      if (selectedTab === 'talents') renderAll();
    }).catch(() => {
      // Lernstatus-Fehler stumm ignorieren
    });

    api.get<DebtStatus>('/talents/debt').then((data) => {
      debtStatus = data;
      if (selectedTab === 'talents') renderAll();
    }).catch(() => {
      // Schulden-Status-Fehler stumm ignorieren
    });

    // DIE KLADDE (NT-TUTORIAL-KLADDE-01) — rein ableitend, Fehler blockieren nicht die restliche UI
    fetchKladdeState().then((data) => {
      kladdeState = data;
      if (selectedTab === 'jobs' && jobsView === 'list') renderAll();
    }).catch(() => {
      // Kladde-Fehler stumm ignorieren — Panel bleibt einfach unsichtbar
    });

    // Lernstatus-Polling alle 60 Sekunden (WebSocket-Fallback bis Phase 4)
    learningPollingTimer ??= setInterval(() => { void syncLearningStatus(); }, 60_000);

    renderAll();
  } catch (err) {
    if (err instanceof ApiError) showToast(err.message, 'error');
    else console.error('[Dashboard] Fehler beim Laden der Dashboard-Daten:', err);
    // Sicherstellen dass Ladeansicht immer verschwindet
    renderAll();
  }
}

// ─── Overlays & Toasts ────────────────────────────────────────────────────────

/** Offline-Zusammenfassung als Modal anzeigen (einmalig nach Login) */
function showOfflineModal(summary: OfflineSummary): void {
  // Kein Modal wenn weniger als 5 Minuten offline UND keine Schicht abgeschlossen
  if (summary.offlineHours < 5 / 60 && !summary.jobSummaries.some(js => js.shiftCompleted)) return;

  const root = document.getElementById('dashboard-root');
  if (root === null) return;

  isRecapModalOpen = true;

  const hasEarnings = summary.jobSummaries.length > 0 && parseInt(summary.totalEarnedCents, 10) > 0;
  const capPct = Math.min(100, Math.floor((summary.offlineHours / summary.offlineCapHours) * 100));

  const modal = document.createElement('div');
  modal.className = 'modal-backdrop';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Offline-Zusammenfassung');
  modal.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>OFFLINE-FORTSCHRITT</h2>
        <button class="modal-close" data-close-modal aria-label="Schließen">&#10005;</button>
      </div>
      <div class="modal-body">
        <p class="modal-subtitle">Du warst <strong>${summary.offlineHours.toFixed(1)}</strong> Stunden offline.</p>

        ${hasEarnings ? `
          <div class="offline-job-summaries">
            ${summary.jobSummaries.map((js) => `
              <div class="offline-job-entry">
                <span class="offline-job-name">${esc(js.jobName.toUpperCase())}</span>
                <span class="offline-job-ticks">${String(js.ticksProcessed)} Ticks</span>
                <span class="offline-job-earned val-gold">+${formatCents(js.earnedCents)}&nbsp;&#8358;</span>
                ${js.shiftCompleted ? '<span class="offline-job-completed val-green">&#10003; SCHICHT</span>' : ''}
              </div>
            `).join('')}
          </div>
          <div class="offline-totals">
            <div class="modal-stat">
              <span class="modal-stat-label">GESAMT VERDIENT</span>
              <span class="modal-stat-value val-gold">+${formatCents(summary.totalEarnedCents)}&nbsp;&#8358;</span>
            </div>
            ${summary.totalEarnedXp > 0 ? `
            <div class="modal-stat">
              <span class="modal-stat-label">GESAMT XP</span>
              <span class="modal-stat-value">+${summary.totalEarnedXp.toLocaleString('de-DE')}</span>
            </div>` : ''}
          </div>
        ` : '<p class="dash-empty">Kein aktiver Job \u2013 keine Offline-Einnahmen.</p>'}

        ${summary.pfandSchicht != null ? `
          <div class="offline-totals">
            <div class="modal-stat">
              <span class="modal-stat-label">EINKAUFSWAGEN</span>
              <span class="modal-stat-value" style="color:var(--accent-purple)">+${String(summary.pfandSchicht.collected)} FLASCHEN GESAMMELT</span>
            </div>
            ${summary.pfandSchicht.leftBehind > 0 ? `
            <div class="modal-stat">
              <span class="modal-stat-label">WAGEN VOLL</span>
              <span class="modal-stat-value val-muted">${String(summary.pfandSchicht.leftBehind)} LIEGEN GELASSEN</span>
            </div>` : ''}
          </div>
        ` : ''}

        <div class="offline-cap-bar-wrapper">
          <div class="offline-cap-bar-label">
            <span>OFFLINE-CAP</span>
            <span class="val-muted">${summary.offlineHours.toFixed(1)}h&nbsp;/&nbsp;${String(summary.offlineCapHours)}h</span>
          </div>
          <div class="offline-cap-bar">
            <div class="offline-cap-fill" style="width:${String(capPct)}%"></div>
          </div>
          ${summary.nextCapUnlock !== null ? `<p class="offline-cap-hint val-muted">Cap erhöhen: ${esc(summary.nextCapUnlock)}</p>` : ''}
        </div>
      </div>
      <div class="modal-footer">
        <button class="primary" data-close-modal>VERSTANDEN</button>
      </div>
    </div>
  `;
  // isRecapModalOpen zurücksetzen wenn Modal geschlossen wird
  modal.addEventListener('click', (e) => {
    if ((e.target as Element).closest('[data-close-modal]') !== null ||
        (e.target as HTMLElement).classList.contains('modal-backdrop')) {
      isRecapModalOpen = false;
    }
  });

  root.appendChild(modal);
}

/** Schicht-Abschluss-Ergebnis als Modal anzeigen */
function showCompleteResult(result: CompleteJobResult): void {
  const root = document.getElementById('dashboard-root');
  if (root === null) return;

  const s = result.summary;

  // Nachvollziehbarkeit (Spieler-Feedback: "18,75 +40% sind keine 49,22 ₦") — bisher zeigte das
  // Modal nur den Job-Level-Bonus, obwohl Talent- und Crime-Stat-Boni ebenfalls multiplikativ in
  // finalRewardCents einfließen. Jeder Bonus bekommt jetzt eine eigene, klar benannte Zeile;
  // ZWISCHENSUMME NACH BONI wird exakt (nicht approximiert) aus den echten Kontobuchungen
  // hergeleitet (final + Schuldentilgung − Events), damit die Summe immer aufgeht.
  const jobLevelBonusPercent = Math.round((s.jobLevelMultiplier - 1) * 100);
  const talentBonusPercent = Math.round((s.talentMultiplier - 1) * 100);
  const crimeStatBonusPercent = Math.round((s.crimeStatMultiplier - 1) * 100);
  const boniRows: string[] = [];
  if (jobLevelBonusPercent > 0) {
    boniRows.push(`<div class="summary-row summary-row--bonus">
        <span class="summary-label">JOB-LEVEL BONUS</span>
        <span class="summary-value val-green">+${String(jobLevelBonusPercent)}%</span>
       </div>`);
  }
  if (talentBonusPercent > 0) {
    boniRows.push(`<div class="summary-row summary-row--bonus">
        <span class="summary-label">TALENT-BONUS</span>
        <span class="summary-value val-green">+${String(talentBonusPercent)}%</span>
       </div>`);
  }
  if (crimeStatBonusPercent > 0) {
    boniRows.push(`<div class="summary-row summary-row--bonus">
        <span class="summary-label">STRASSEN-RUF-BONUS</span>
        <span class="summary-value val-green">+${String(crimeStatBonusPercent)}%</span>
       </div>`);
  }
  // ZWISCHENSUMME = finaler Kontostand-Zuwachs + Schuldentilgung (die vom Konto abgezogen wurde,
  // bevor sie überhaupt ankam) − Event-Boni (die separat weiter unten aufgeführt werden).
  const subtotalAfterBoniCents =
    BigInt(s.finalRewardCents) + BigInt(s.debtRepaymentCents) - BigInt(s.eventBonusTotal);
  const multiplierHtml = boniRows.length > 0
    ? `${boniRows.join('')}
       <div class="summary-row">
        <span class="summary-label">ZWISCHENSUMME NACH BONI</span>
        <span class="summary-value">${formatCents(subtotalAfterBoniCents.toString())}&nbsp;&#8358;</span>
       </div>`
    : '';

  // Events dieser Schicht
  const eventsHtml = s.events.length > 0
    ? `<div class="summary-section">
        <div class="summary-section-label">EVENTS DIESER SCHICHT</div>
        ${s.events.map((ev) => {
          if (ev.bonusCents == null || ev.bonusCents === '0') {
            return `<div class="summary-event"><span class="summary-event-name">${esc(ev.name)}</span></div>`;
          }
          const signed = formatSignedCents(ev.bonusCents);
          const bonus = `<span class="summary-event-bonus ${signed.colorClass}">${signed.text}&nbsp;&#8358;</span> <span class="text-muted summary-event-received">(bereits erhalten)</span>`;
          return `<div class="summary-event"><span class="summary-event-name">${esc(ev.name)}</span>${bonus}</div>`;
        }).join('')}
       </div>`
    : '';

  // Item-Drop (Schicht-Abschluss-Drop)
  const itemDropHtml = (() => {
    const d = s.itemDrop;
    if (d.itemType === null) return '';
    const label = esc(d.itemLabel ?? d.itemType);
    const qty = d.quantity > 0 ? d.quantity : 1;
    if (d.destinationType === 'AUTO_SOLD') {
      const priceStr = d.autoSoldCents !== undefined ? ` · ${formatCents(String(d.autoSoldCents))} ₦` : '';
      return `<div class="summary-section">
        <div class="summary-row">
          <span class="summary-label">ITEM GEFUNDEN</span>
          <span class="summary-value" style="color:var(--accent-orange)">${String(qty)}x ${label} AUTO-VERKAUFT${esc(priceStr)}</span>
        </div>
      </div>`;
    }
    if (d.dropped) {
      return `<div class="summary-section">
        <div class="summary-row">
          <span class="summary-label">ITEM GEFUNDEN</span>
          <span class="summary-value" style="color:var(--accent-purple)">+${String(qty)}x ${label}</span>
        </div>
      </div>`;
    }
    if (d.overflowReason === 'STORAGE_FULL' || d.overflowReason === 'NO_STORAGE') {
      return `<div class="summary-section">
        <div class="summary-row">
          <span class="summary-label">ITEM GEFUNDEN</span>
          <span class="summary-value text-muted">${String(qty)}x ${label} (${d.overflowReason === 'NO_STORAGE' ? 'kein Lager' : 'Lager voll'})</span>
        </div>
      </div>`;
    }
    return '';
  })();

  // NT-PFAND-SCHICHT-01: Flaschen, die der Einkaufswagen während der Schicht gesammelt hat.
  const pfandSchichtHtml = (() => {
    const p = s.pfandSchicht;
    if (p == null) return '';
    const leftHtml = p.leftBehind > 0
      ? `<div class="summary-row">
          <span class="summary-label">WAGEN VOLL</span>
          <span class="summary-value text-muted">${String(p.leftBehind)} LIEGEN GELASSEN</span>
        </div>`
      : '';
    return `<div class="summary-section">
        <div class="summary-row">
          <span class="summary-label">EINKAUFSWAGEN</span>
          <span class="summary-value" style="color:var(--accent-purple)">+${String(p.collected)} FLASCHEN GESAMMELT</span>
        </div>
        ${leftHtml}
      </div>`;
  })();

  // Schuldentilgung (automatischer, sonst unsichtbarer Abzug — Spieler-Feedback: "nicht der
  // volle Betrag bekommen"): 10% des Brutto-Verdienstes fließen ohne Zutun in bestehende Schulden.
  const debtRepaymentHtml = s.debtRepaymentCents !== '0'
    ? `<div class="summary-section">
        <div class="summary-row">
          <span class="summary-label">SCHULDENTILGUNG</span>
          <span class="summary-value val-red">-${formatCents(s.debtRepaymentCents)}&nbsp;&#8358;</span>
        </div>
      </div>`
    : '';

  // NT-JOB-LAEUFER-01: zusätzlicher, immer BLACK_MONEY Bonus-Anteil (job.rewardBlackCents) —
  // eigene Zeilen mit "S-₦"-Suffix, da GESAMT ERHALTEN/NEUES KONTO unten ausschließlich den
  // primären (earnedCurrency) Betrag zeigen und diesen nicht vermischen dürfen.
  const blackMoneyBonusHtml = s.bonusBlackMoneyCents !== '0'
    ? `<div class="summary-row">
        <span class="summary-label">S-&#8358;-SCHNITT</span>
        <span class="summary-value val-bm">+${formatCents(s.bonusBlackMoneyCents)}&nbsp;S-&#8358;</span>
       </div>
       <div class="summary-row">
        <span class="summary-label">NEUES SCHWARZGELD-KONTO</span>
        <span class="summary-value val-bm">${formatCents(s.newBlackMoneyBalance)}&nbsp;S-&#8358;</span>
       </div>`
    : '';

  // Job-Level-Up
  const jobLevelUpHtml = s.jobLevelUp !== null
    ? `<div class="summary-row summary-row--levelup">
        <span class="summary-label">JOB-LEVEL UP!</span>
        <span class="summary-value val-gold">Level ${String(s.jobLevelUp.newJobLevel)}</span>
       </div>`
    : '';

  // Player-Level-Up
  const playerLevelUpHtml = s.playerLevelUp !== null
    ? `<div class="summary-row summary-row--levelup">
        <span class="summary-label">LEVEL UP!</span>
        <span class="summary-value val-gold">LVL ${String(s.playerLevelUp.oldLevel)} &rarr; ${String(s.playerLevelUp.newLevel)}</span>
       </div>`
    : '';

  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal modal--shift-summary modal--no-dismiss">
      <div class="modal-header">
        <h2>SCHICHT ABGESCHLOSSEN</h2>
      </div>
      <div class="modal-body">
        <div class="summary-meta">
          <span class="summary-job-name">${esc(s.jobName)}</span>
          <span class="summary-meta-sep">&bull;</span>
          <span>${String(s.shiftDuration)}&nbsp;Min</span>
          <span class="summary-meta-sep">&bull;</span>
          <span>${String(s.completionPercent)}%</span>
        </div>

        <div class="summary-section">
          <div class="summary-row">
            <span class="summary-label">GRUNDVERDIENST</span>
            <span class="summary-value">${formatCents(s.baseRewardCents)}&nbsp;&#8358;</span>
          </div>
          ${multiplierHtml}
        </div>

        <div class="summary-section">
          <div class="summary-row">
            <span class="summary-label">XP VERDIENT</span>
            <span class="summary-value val-blue">+${s.xpEarned.toLocaleString('de-DE')}&nbsp;XP</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">JOB-XP</span>
            <span class="summary-value val-blue">+${s.jobXpEarned.toLocaleString('de-DE')}</span>
          </div>
          ${jobLevelUpHtml}
          ${playerLevelUpHtml}
        </div>

        ${eventsHtml}
        ${itemDropHtml}
        ${pfandSchichtHtml}
        ${debtRepaymentHtml}

        <div class="summary-section summary-section--total">
          <div class="summary-row summary-row--total">
            <span class="summary-label">GESAMT ERHALTEN</span>
            <span class="summary-value val-gold summary-total-value">+${formatCents(s.finalRewardCents)}&nbsp;&#8358;</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">NEUES KONTO</span>
            <span class="summary-value val-gold">${formatCents(s.newBalance)}&nbsp;&#8358;</span>
          </div>
          ${blackMoneyBonusHtml}
          <div class="summary-row">
            <span class="summary-label">N&#196;CHSTE SCHICHT IN</span>
            <span class="summary-value val-orange" id="summary-cooldown">${formatCountdown(s.cooldownEndsAt)}</span>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="primary" id="summary-confirm-btn">VERSTANDEN</button>
      </div>
    </div>
  `;
  root.appendChild(overlay);

  // Cooldown-Countdown live aktualisieren
  const cooldownInterval = setInterval(() => {
    const el = document.getElementById('summary-cooldown');
    if (el === null) { clearInterval(cooldownInterval); return; }
    el.textContent = formatCountdown(s.cooldownEndsAt);
  }, 1_000);

  // Modal nach 5 Minuten automatisch schließen (ohne Nutzerinteraktion)
  const autoDismissTimer = setTimeout(() => {
    clearInterval(cooldownInterval);
    overlay.remove();
  }, 5 * 60 * 1_000);

  // Confirm-Button: Modal schließen + Header-Balance aktualisieren
  const confirmBtn = overlay.querySelector<HTMLButtonElement>('#summary-confirm-btn');
  confirmBtn?.addEventListener('click', () => {
    clearTimeout(autoDismissTimer);
    clearInterval(cooldownInterval);
    overlay.remove();
    // Header-Balance auf neuen Stand bringen (Schicht ist vorbei)
    document.querySelectorAll<HTMLElement>('.val-gold[title="Bargeld"]').forEach((el) => {
      el.textContent = `${formatCents(s.newBalance)}\u00a0\u20ac`;
    });
  });
}

/** Notification-Sound bei DECISION/REACTION abspielen.
 *  Sound-Datei: apps/frontend/public/sounds/decision_ping.mp3
 *  Schlägt stumm fehl wenn die Datei noch nicht vorhanden ist. */
function playDecisionSound(): void {
  try {
    const volume = getSoundVolumeFactor();
    if (volume <= 0) return; // Auf 0% gestellt — Sound bewusst stumm, kein Audio-Element nötig
    const audio = new Audio('/sounds/decision_ping.mp3');
    audio.volume = volume;
    void audio.play().catch(() => { /* Sound nicht verfügbar */ });
  } catch {
    // Kein Fehler wenn Audio nicht unterstützt
  }
}

/** Event-Popup anzeigen (Tick-Response mit Event-Payload) */
function showEventPopup(event: EventPayload): void {
  // Bestehendes Event-Popup entfernen
  document.getElementById('event-popup')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop event-popup-backdrop';
  overlay.id = 'event-popup';

  // Bonuszeile – nur bei PASSIVE-Events mit Geld-Effekt (bereits erhalten). Vorzeichen-sicher
  // formatiert (BONUS_CENTS-Effekte können auch negativ sein, z.B. Strafen) — ein hart codiertes
  // "+" hätte bei negativen Beträgen "+-3,00 ₦" ergeben statt "-3,00 ₦".
  const eventBonusCents = event.bonusCents;
  const bonusHtml = (event.type === 'PASSIVE' && eventBonusCents != null && eventBonusCents !== '0')
    ? (() => {
        const signed = formatSignedCents(eventBonusCents);
        return `<div class="event-reward"><span class="${signed.colorClass}">${signed.text}&nbsp;&#8358;</span> <span class="text-muted event-already-received">(bereits erhalten)</span></div>`;
      })()
    : '';

  // Ruf-Zeile – nur bei PASSIVE-Events mit Ruf-Änderung
  const repDelta = event.reputationDelta;
  const repHtml = (event.type === 'PASSIVE' && repDelta !== null && repDelta !== 0)
    ? `<div class="event-reward"><span style="color:${repDelta > 0 ? 'var(--accent-green)' : 'var(--accent-red)'}">Ruf ${repDelta > 0 ? '+' : ''}${String(repDelta)}</span> <span class="text-muted event-already-received">(bereits erhalten)</span></div>`
    : '';

  // Item-Drop-Zeilen – nur bei PASSIVE-Events mit Item-Drops (ersetzen Standalone-Toasts)
  const itemDropsHtml = (event.type === 'PASSIVE' && event.itemDropResults && event.itemDropResults.length > 0)
    ? event.itemDropResults
        .filter((d) => d.itemType !== null)
        .map((d) => {
          const label = esc(d.itemLabel ?? d.itemType ?? '?');
          const qty = d.quantity > 0 ? d.quantity : 1;
          if (d.destinationType === 'AUTO_SOLD') {
            const priceStr = d.autoSoldCents !== undefined ? ` (${formatCents(String(d.autoSoldCents))} ₦)` : '';
            return `<div class="event-reward"><span style="color:var(--accent-orange)">${String(qty)}x ${label} AUTO-VERKAUFT${esc(priceStr)}</span> <span class="text-muted event-already-received">(kein Lager)</span></div>`;
          }
          if (d.dropped) {
            return `<div class="event-reward"><span style="color:var(--accent-purple)">+${String(qty)}x ${label}</span> <span class="text-muted event-already-received">(Inventar)</span></div>`;
          }
          return `<div class="event-reward"><span class="text-muted">${String(qty)}x ${label} liegengelassen</span></div>`;
        })
        .join('')
    : '';

  if (event.type === 'PASSIVE') {
    const logHtml = event.logMessage
      ? `<p class="event-log-message">${esc(event.logMessage)}</p>`
      : '';
    overlay.innerHTML = `
      <div class="modal modal--event modal--passive">
        <div class="modal-header">
          <span class="event-type-badge">PASSIV</span>
          <h3>${esc(event.name)}</h3>
        </div>
        <div class="modal-body">
          <p>${esc(event.description)}</p>
          ${logHtml}
          ${bonusHtml}
          ${repHtml}
          ${itemDropsHtml}
        </div>
        <div class="modal-footer">
          <button class="primary" id="event-popup-ok">OK</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    // OK-Button: manuell schließen
    overlay.querySelector<HTMLButtonElement>('#event-popup-ok')?.addEventListener('click', () => {
      overlay.remove();
    });
    // Fallback: automatisch nach 5 Minuten schließen
    const passiveTimer = setTimeout(() => { overlay.remove(); }, 300_000);
    overlay.querySelector<HTMLButtonElement>('#event-popup-ok')?.addEventListener('click', () => {
      clearTimeout(passiveTimer);
    }, { once: true });
    return;
  }

  if (event.type === 'DECISION' && event.options !== null && event.options.length >= 1) {
    playDecisionSound();
    const expiresAtMs = event.expiresAt ? new Date(event.expiresAt).getTime() : Date.now() + 90_000;
    const getRemaining = (): number => Math.max(0, Math.round((expiresAtMs - Date.now()) / 1000));
    let countdown = getRemaining();
    const buttonsHtml = event.options
      .map((opt, i) => `<button class="${i === 0 ? 'secondary' : 'primary'}" data-event-choice="${esc(opt.key)}" data-event-log-id="${esc(event.jobEventLogId)}">${esc(opt.label)}</button>`)
      .join('');
    overlay.innerHTML = `
      <div class="modal modal--event modal--decision">
        <div class="modal-header">
          <span class="event-type-badge event-type-badge--decision">ENTSCHEIDUNG</span>
          <h3>${esc(event.name)}</h3>
          <span class="event-countdown" id="event-countdown">${String(countdown)}s</span>
        </div>
        <div class="modal-body">
          <p>${esc(event.description)}</p>
        </div>
        <div class="modal-footer modal-footer--two">${buttonsHtml}</div>
      </div>
    `;
    document.body.appendChild(overlay);
    const countdownTimer = setInterval(() => {
      countdown = getRemaining();
      const el = document.getElementById('event-countdown');
      if (el !== null) el.textContent = `${String(countdown)}s`;
      if (countdown <= 0) {
        clearInterval(countdownTimer);
        overlay.remove();
        // Kein API-Aufruf – Server löst mit DEFAULT-Option auf beim nächsten Tick
      }
    }, 1_000);
    overlay.addEventListener('click', (e) => {
      const btn = (e.target as Element).closest<HTMLButtonElement>('[data-event-choice]');
      if (btn === null) return;
      clearInterval(countdownTimer);
      const choiceKey = btn.dataset.eventChoice ?? '';
      overlay.remove();
      if (choiceKey) void handleEventRespond(event.jobEventLogId, choiceKey);
    });
    return;
  }

  if (event.type === 'REACTION') {
    playDecisionSound();
    const expiresAtMs = event.expiresAt ? new Date(event.expiresAt).getTime() : Date.now() + 30_000;
    const getRemaining = (): number => Math.max(0, Math.round((expiresAtMs - Date.now()) / 1000));
    let countdown = getRemaining();
    overlay.innerHTML = `
      <div class="modal modal--event modal--reaction">
        <div class="modal-header">
          <span class="event-type-badge event-type-badge--reaction">REAKTION</span>
          <h3>${esc(event.name)}</h3>
          <span class="event-countdown" id="event-countdown">${String(countdown)}s</span>
        </div>
        <div class="modal-body">
          <p>${esc(event.description)}</p>
        </div>
        <div class="modal-footer">
          <button class="primary btn-reaction-click" data-event-choice="A" data-event-log-id="${esc(event.jobEventLogId)}">JETZT KLICKEN!</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const countdownTimer = setInterval(() => {
      countdown = getRemaining();
      const el = document.getElementById('event-countdown');
      if (el !== null) el.textContent = `${String(countdown)}s`;
      if (countdown <= 0) {
        clearInterval(countdownTimer);
        overlay.remove();
        // Kein API-Aufruf – Server löst neutral auf beim nächsten Tick
      }
    }, 1_000);
    overlay.addEventListener('click', (e) => {
      const btn = (e.target as Element).closest<HTMLButtonElement>('[data-event-choice]');
      if (btn === null) return;
      clearInterval(countdownTimer);
      overlay.remove();
      void handleEventRespond(event.jobEventLogId, 'A');
    });
  }
}

const showToast = showGlobalToast;

// ─── Einstiegspunkt ───────────────────────────────────────────────────────────

/**
 * Page-Visibility-Handler: Wird ausgelöst wenn der Tab/Browser wieder aktiv wird.
 * Löst umgehend Status-Sync aus um Timer-Drift (Opera, Safari PWA) zu korrigieren.
 * Schicht-Ende wird server-seitig geprüft – kein Client-Clock-Vertrauen.
 */
function handlePageVisible(): void {
  if (!isMounted()) return;
  if (document.visibilityState !== 'visible') return;

  // Kein Auto-Complete solange Offline-Recap-Modal offen ist
  if (isRecapModalOpen) {
    void syncJobStatus();
    return;
  }

  // Wenn Schicht abgelaufen: zuerst Server-Sync, DANN abschließen.
  // Verhindert dass offline-verarbeitete Jobs nochmal durch den Online-Handler laufen.
  if (currentActiveJob?.shiftEndsAt != null && !isCompletingJob) {
    if (Date.now() >= new Date(currentActiveJob.shiftEndsAt).getTime()) {
      void syncJobStatus().then(() => {
        if (currentActiveJob?.shiftEndsAt != null && !isCompletingJob &&
            Date.now() >= new Date(currentActiveJob.shiftEndsAt).getTime()) {
          void handleCompleteJob();
        }
        startProgressTimer();
      }).catch(() => { startProgressTimer(); });
      return;
    }
  }

  // Server-Sync erzwingen um Countdown-Drift zu beheben und verpasste Ticks nachzuholen
  // Timer NACH dem Sync starten, damit Cooldowns korrekt geladen sind (PWA-Fix)
  void syncJobStatus().then(() => {
    startProgressTimer();
    // Tick neu planen mit frischen Server-Daten (Mobile: Timer-Drift durch Sleep/Background beheben)
    if (currentActiveJob !== null) {
      scheduleNextTick(Math.max(0, currentActiveJob.nextTickInSeconds));
    }
  }).catch(() => {
    // Fehler ignorieren – Timer trotzdem starten mit alten Daten
    startProgressTimer();
  });
}

export function renderDashboard(): void {
  const player = gameState.getPlayer();
  if (player === null) { navigate('/login'); return; }

  // Wenn Dashboard bereits gemountet ist → nur Tab wechseln, kein vollständiger Re-Mount.
  // Verhindert dass Polling-Timer und API-Calls bei jedem Nav-Klick neu starten.
  if (isMounted()) {
    const path = window.location.pathname;
    const newTab = tabForPath(path);
    if (newTab !== selectedTab) {
      leaveCurrentTab();
      selectedTab = newTab;
      enterTab(newTab, path);
      renderAll();
      return;
    }

    // Gleicher Tab, andere Unteransicht (NT-LAYOUT-01): Jobs-Unteransicht bzw. Crime-Ops-Sektion
    if (newTab === 'jobs') {
      const view = getJobsViewForPath(path) ?? 'list';
      if (view !== jobsView) {
        leaveJobsView();
        enterJobsView(view);
        renderAll();
      }
    } else if (newTab === 'crime') {
      if (getCrimeOpsSectionForPath(path) !== null) {
        // Neu mounten: mountCrimeOpsTab() übernimmt die Sektion aus dem Pfad
        cleanupCrimeOps();
        renderAll();
      } else {
        // /business/crime-ops: aktive Sektion bleibt, URL wird ihr wieder angeglichen
        syncCrimeOpsUrlToSelection();
      }
    } else if (newTab === 'kladde' && window.location.search !== '') {
      // Deep-Link (?view=spickzettel&entry=…) auch dann auswerten, wenn die Kladde schon offen ist
      prepareKladdeTab();
      renderAll();
    }
    return;
  }

  // Vollständiger Mount: Tab aus URL ableiten
  selectedTab = tabForPath(window.location.pathname);

  // Mobile: Tab aus sessionStorage überschreiben (BottomNav setzt diesen Wert)
  const requestedTab = sessionStorage.getItem('_nt_dashboardTab') as Tab | null;
  if (requestedTab !== null) {
    selectedTab = requestedTab;
    sessionStorage.removeItem('_nt_dashboardTab');
  }

  // Offline-Summary einmalig lesen und sofort aus State entfernen
  const offlineSummary = player.offlineSummary ?? null;
  if (offlineSummary !== null) {
    gameState.setPlayer({ ...player, offlineSummary: null });
  }

  // Gecachte Daten merken BEVOR cleanup() sie löscht (z.B. nach Settings → Dashboard)
  const hasCachedData = talentTree !== null;

  cleanup();
  enterTab(selectedTab, window.location.pathname);

  // Page-Visibility-Listener registrieren (Timer-Drift-Schutz für Opera/Safari PWA)
  visibilityHandler = handlePageVisible;
  document.addEventListener('visibilitychange', visibilityHandler);

  // Dashboard-Grundstruktur aufbauen (erstellt #dashboard-root im DOM)
  const app = document.getElementById('app');
  if (app === null) return;
  app.innerHTML = `
    <div class="dashboard" id="dashboard-root">
      ${renderHeader(player)}
      <div class="dash-body">
        ${renderNav()}
        <main class="dash-main">
          <section class="dash-content">
            <p class="dash-empty">Lade Daten...</p>
          </section>
        </main>
        ${renderSidebar(player)}
      </div>
    </div>
  `;

  attachListeners();
  initHamburgerMenu();
  initNotificationBell();
  initScrollToTop();
  updateScrollToTopContainer();
  startStatusPolling();

  headerResizeHandler = () => { syncHeaderHeightVar(); };
  window.addEventListener('resize', headerResizeHandler);
  window.requestAnimationFrame(syncHeaderHeightVar);

  if (hasCachedData) {
    // Gecachte Daten vorhanden (z.B. Rückkehr von Settings) → sofort rendern, Hintergrund-Refresh
    renderAll();
    void loadDashboardData().then(() => {
      if (offlineSummary !== null) showOfflineModal(offlineSummary);
    });
  } else {
    // Erstmaliges Laden → Lade-Platzhalter bleibt bis Daten da sind
    void loadDashboardData().then(() => {
      if (offlineSummary !== null) showOfflineModal(offlineSummary);
    });
  }
}
